import React from 'react';
import { useAuth } from '../../context/AuthContext';
import { useMonthlyPerformance } from '../overviewV9/hooks/useMonthlyPerformance';
import { HedeflenenCirTrafikTable } from '../overviewV9/components/historical/HedeflenenCirTrafikTable';

export default function BrandingPlanRegalien() {
  const { selectedBrand } = useAuth();
  const brandId = selectedBrand?.id || (typeof window !== 'undefined' ? localStorage.getItem('currentBrandId') : null);
  const monthly = useMonthlyPerformance(brandId, 'all', null, null);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-10">

      {/* ───────── Page Header ───────── */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="bg-[#111] p-6 text-white flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-5">
            <div className="bg-white rounded-lg px-4 py-2">
              <span className="text-xl font-black tracking-widest text-gray-900">RÉGALIEN</span>
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight">Régalien</h1>
              <p className="text-sm text-neutral-400 mt-0.5">Dijital Pazarlama Stratejisi — Niş Parfüm Evi</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <span className="inline-flex items-center px-3 py-1 bg-white/10 text-xs font-medium rounded-full border border-white/20">regalien.com</span>
            <span className="inline-flex items-center px-3 py-1 bg-white/10 text-xs font-medium rounded-full border border-white/20">Niş Parfüm · İstanbul</span>
          </div>
        </div>
      </div>

      {/* ───────── Brand Snapshot ───────── */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-bold text-gray-900">Marka Özeti</h2>
        </div>
        <div className="p-6">
          <p className="text-sm text-gray-700 leading-relaxed mb-6">
            <strong>Régalien</strong>, İstanbul merkezli bir niş parfüm evidir. İsmi Fransızca "krallığa ait" anlamına gelir. Doğu ile Batı'nın özünü bir araya getirerek, Osmanlı mirası ve Türk kültüründen ilham alan zamansız kokular yaratır.
            Tüm ürünler <strong>Extrait de Parfum</strong> konsantrasyonunda üretilir. Fiyat aralığı <strong>₺8.500–₺9.200</strong> (80ml). Avrupa'da €210–220 seviyesinde konumlanır.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {['Exclusive Collection', 'Heritage Collection', 'Lucky Collection', 'Turkish Bath Rituals'].map(col => (
              <div key={col} className="rounded-lg border p-3 text-center">
                <span className="text-sm font-bold text-gray-900">{col}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
            {['Şah', 'Turkuaz', 'Hatır', 'Dem', 'Hazz', 'Nazen', 'Ceremony Flu', 'Kashmeran', 'Diamond of Velvet', 'Châtelain 22'].map(p => (
              <div key={p} className="rounded border px-2 py-1.5 text-center">
                <span className="text-xs font-medium text-gray-700">{p}</span>
              </div>
            ))}
          </div>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="rounded-lg border p-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Rakipler</p>
              <p className="text-sm text-gray-900 font-medium">Nishane · Byredo · Le Labo · Vilhelm · Xerjoff · Amouage</p>
            </div>
            <div className="rounded-lg border p-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Konumlandırma</p>
              <p className="text-sm text-gray-900 font-medium">Ultra-premium niş parfüm evi — Türk kültürel mirası & modern parfümeri sentezi</p>
            </div>
            <div className="rounded-lg border p-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Satış Kanalları</p>
              <p className="text-sm text-gray-900 font-medium">regalien.com (WooCommerce) · Nokta Cosmetics · Dekant Parfüm · Avrupa niş butikler</p>
            </div>
          </div>
        </div>
      </div>

      {/* ───────── Hedef Kitle Özellikleri ───────── */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-bold text-gray-900">Hedef Kitle Özellikleri</h2>
        </div>
        <div className="p-6">
          <div className="rounded-lg border p-5 mb-5">
            <h3 className="text-sm font-bold text-gray-900 mb-4 uppercase tracking-wider">Demografik & Profesyonel Profil</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3 text-sm text-gray-700">
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Yaş Aralığı</p>
                <p><strong>28–55</strong> ağırlıklı, çekirdek segment <strong>32–48</strong></p>
              </div>
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Cinsiyet</p>
                <p>Kadın <strong>45%</strong> — Erkek <strong>55%</strong></p>
              </div>
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Gelir Segmenti</p>
                <p>Üst gelir grubu (<strong>A, A+</strong>) — lüks tüketime alışkın</p>
              </div>
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Eğitim</p>
                <p>Üniversite ve üstü — kültürel farkındalığı yüksek, <strong>beyaz yaka</strong> veya iş sahibi</p>
              </div>
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Lokasyon</p>
                <p>İstanbul (ağırlıklı), Ankara, İzmir + <strong>Avrupa & Körfez ülkeleri</strong> (export)</p>
              </div>
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Meslek Profili</p>
                <p>Üst düzey yöneticiler, girişimciler, avukatlar, doktorlar, sanat & kreatif profesyonelleri</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
            <div className="rounded-lg border p-5">
              <h3 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wider">Psikografik Profil</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li>· Kokuyu <strong>bir kimlik ifadesi</strong> olarak görür — sadece güzel kokma değil, kendini anlatma aracı</li>
                <li>· <strong>Hikaye ve miras</strong> arar — parfümün arkasındaki kültürel anlam önemli</li>
                <li>· Fiyat hassasiyeti çok düşük; <strong>"herkesin kullandığı"</strong> değil <strong>"kimsenin bilmediği"</strong> olanı arar</li>
                <li>· <strong>Koleksiyoner ruhu</strong> — niş parfüm koleksiyonu biriktirmeye yatkın</li>
                <li>· Sanat, mimari, edebiyat ve tarihe ilgi duyar — estetik duyarlılık yüksek</li>
                <li>· <strong>Exclusivity</strong> odaklı — sınırlı üretim, nadir bileşenler, butik deneyim</li>
                <li>· Detaylara dikkat eder — şişe tasarımı, paketleme, sunum deneyimi değerli</li>
              </ul>
            </div>
            <div className="rounded-lg border p-5">
              <h3 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wider">Satın Alma Davranışı</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li>· Ortalama sepet tutarı <strong>₺8.500–₺18.000</strong> aralığında (1-2 şişe)</li>
                <li>· Yılda <strong>2–4 kez</strong> niş parfüm satın alır, yeni koleksiyonlarda pikler</li>
                <li>· Satın alma öncesi <strong>detaylı araştırma</strong> — Fragrantica, Parfumo, YouTube review</li>
                <li>· <strong>Dekant/discovery set</strong> ile deneme sonrası tam şişe satın alma paterni</li>
                <li>· <strong>Hediye motivasyonu</strong> çok güçlü — özel günlerde niş parfüm hediye etme trendi</li>
                <li>· <strong>Mağaza deneyimi</strong> kritik — koklamadan almak istemeyen profil</li>
                <li>· Sadakat çok yüksek — beğendiği evden tekrar tekrar alır, koleksiyon tamamlar</li>
                <li>· Uluslararası alışveriş deneyimi var — Avrupa seyahatlerinde niş butik ziyareti</li>
              </ul>
            </div>
          </div>

          <div className="rounded-lg border p-5">
            <h3 className="text-sm font-bold text-gray-900 mb-1 uppercase tracking-wider">Hedeflenebilir İlgi Alanları & Sinyaller</h3>
            <p className="text-xs text-gray-400 mb-4">Dijital reklamlarda targeting, lookalike ve custom audience oluşturmada kullanılabilir sinyaller</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <p className="text-xs font-bold text-gray-900 mb-2 uppercase tracking-wider">Parfüm & Güzellik</p>
                <ul className="space-y-1.5 text-sm text-gray-600">
                  <li>· Niş parfüm araştırması (Fragrantica, Parfumo, Basenotes)</li>
                  <li>· Extrait de parfum, EDP, oud, amber, musk arayışları</li>
                  <li>· Sephora, Beymen, Vakko Beauty alışverişçileri</li>
                  <li>· Nishane, Byredo, Le Labo, Maison Francis Kurkdjian takipçileri</li>
                  <li>· Parfüm dekant / discovery set alıcıları</li>
                  <li>· Doğal / organik kozmetik ilgileri</li>
                </ul>
              </div>

              <div>
                <p className="text-xs font-bold text-gray-900 mb-2 uppercase tracking-wider">Lüks Yaşam Tarzı</p>
                <ul className="space-y-1.5 text-sm text-gray-600">
                  <li>· <strong>Beymen, Vakko, Harvey Nichols</strong> müşterileri</li>
                  <li>· Lüks otel konaklamaları (Four Seasons, Edition, Soho House)</li>
                  <li>· Fine dining & gastronomi ilgisi (Michelin, Nusret, Mikla)</li>
                  <li>· Premium araç sahipleri (BMW, Mercedes, Porsche, Tesla)</li>
                  <li>· Sanat galerisi / müze ziyaretçileri</li>
                  <li>· Business/First class seyahat edenler</li>
                </ul>
              </div>

              <div>
                <p className="text-xs font-bold text-gray-900 mb-2 uppercase tracking-wider">Kültür & Sanat</p>
                <ul className="space-y-1.5 text-sm text-gray-600">
                  <li>· İstanbul Modern, Pera Müzesi, Arter ziyaretçileri</li>
                  <li>· Osmanlı tarihi, Türk kültürü ilgi alanı</li>
                  <li>· İç mimari, dekorasyon, tasarım takipçileri</li>
                  <li>· Edebiyat, şiir, felsefe okurları</li>
                  <li>· Klasik müzik, caz konserleri</li>
                  <li>· Antika & koleksiyon meraklıları</li>
                </ul>
              </div>

              <div>
                <p className="text-xs font-bold text-gray-900 mb-2 uppercase tracking-wider">Lokasyon Sinyalleri</p>
                <ul className="space-y-1.5 text-sm text-gray-600">
                  <li>· <strong>Nişantaşı, Bebek, Etiler, Bağdat Caddesi</strong> sakinleri</li>
                  <li>· İstinyePark, Zorlu Center, Akasya AVM premium katları</li>
                  <li>· Kapalıçarşı / Grand Bazaar çevresi (turist + koku meraklısı)</li>
                  <li>· Niş parfüm butikleri yakınında bulunanlar</li>
                  <li>· Beşiktaş, Kadıköy, Cihangir — kreatif sınıf yoğunluğu</li>
                  <li>· Bodrum, Çeşme, Alaçatı — yaz sezonu premium kitle</li>
                </ul>
              </div>

              <div>
                <p className="text-xs font-bold text-gray-900 mb-2 uppercase tracking-wider">Medya & İçerik Tüketimi</p>
                <ul className="space-y-1.5 text-sm text-gray-600">
                  <li>· YouTube: parfüm review, "top 10 niche fragrances", unboxing</li>
                  <li>· Instagram: #nicheperfume, #fragrance, #perfumecollection</li>
                  <li>· TikTok: #perfumetok, "fragrance of the day", koleksiyon videoları</li>
                  <li>· Blog/Dergi: Kafadar, Vogue Türkiye, GQ Türkiye, Elle</li>
                  <li>· Podcast: lüks yaşam, kültür, tasarım</li>
                  <li>· Reddit: r/fragrance, r/nichefragrance</li>
                </ul>
              </div>

              <div>
                <p className="text-xs font-bold text-gray-900 mb-2 uppercase tracking-wider">Arama Sinyalleri (Search Intent)</p>
                <ul className="space-y-1.5 text-sm text-gray-600">
                  <li>· "niş parfüm önerisi"</li>
                  <li>· "en iyi oud parfüm erkek"</li>
                  <li>· "türk niş parfüm markaları"</li>
                  <li>· "extrait de parfum nedir"</li>
                  <li>· "nishane vs regalien"</li>
                  <li>· "hediye parfüm lüks"</li>
                  <li>· "kalıcı erkek parfüm 2026"</li>
                </ul>
              </div>

              <div>
                <p className="text-xs font-bold text-gray-900 mb-2 uppercase tracking-wider">Satın Alma & Marka Sinyalleri</p>
                <ul className="space-y-1.5 text-sm text-gray-600">
                  <li>· Nishane, Xerjoff, Amouage, MFK, Byredo marka arayanlar</li>
                  <li>· Sephora Prestige / Boyner Select parfüm alıcıları (conquest)</li>
                  <li>· Dior, Chanel, Tom Ford'dan <strong>niş'e geçiş</strong> yapanlar</li>
                  <li>· Son 30 günde parfüm kategorisi ziyaret edenler</li>
                  <li>· Discovery set / dekant satın almış kişiler</li>
                  <li>· Parfüm forumlarında aktif üyeler</li>
                </ul>
              </div>

              <div>
                <p className="text-xs font-bold text-gray-900 mb-2 uppercase tracking-wider">Hediye & Özel Gün Sinyalleri</p>
                <ul className="space-y-1.5 text-sm text-gray-600">
                  <li>· "sevgiliye hediye parfüm", "doğum günü hediye lüks"</li>
                  <li>· Sevgililer Günü, Anneler/Babalar Günü, yılbaşı dönemi</li>
                  <li>· Kurumsal hediye arayanlar (VIP müşteri, iş ortağı)</li>
                  <li>· Düğün / nişan hediye araştırması</li>
                  <li>· Premium hediye paketleme, özel kutu talepleri</li>
                  <li>· Gift card / hediye kartı arayanlar</li>
                </ul>
              </div>

              <div>
                <p className="text-xs font-bold text-gray-900 mb-2 uppercase tracking-wider">Wellness & Self-Care</p>
                <ul className="space-y-1.5 text-sm text-gray-600">
                  <li>· Spa & hamam deneyimi arayanlar</li>
                  <li>· Aromaterapi, doğal esanslar ilgisi</li>
                  <li>· Premium cilt bakımı (La Mer, Aesop, Drunk Elephant)</li>
                  <li>· Meditasyon, mindfulness uygulamaları</li>
                  <li>· Organic / clean beauty takipçileri</li>
                  <li>· Ev kokusu, mum, tütsü alıcıları (Diptyque, Byredo Home)</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ───────── Alışveriş Intent Sinyalleri ───────── */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-bold text-gray-900">Çapraz Alışveriş Intent Sinyalleri</h2>
          <p className="text-xs text-gray-400 mt-1">Bu markalardan / kategorilerden alışveriş yapmış veya ilgi gösteren kullanıcılar, Régalien hedef kitlesine yüksek oranda örtüşür</p>
        </div>
        <div className="p-6">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-3 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider w-1/5">Kategori</th>
                <th className="text-left py-3 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider w-2/5">Marka / Platform Örnekleri</th>
                <th className="text-left py-3 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider w-2/5">Intent Sinyali</th>
              </tr>
            </thead>
            <tbody className="text-gray-700">
              <tr className="border-b border-gray-100">
                <td className="py-3 px-4 font-medium text-gray-900">Niş Parfüm Evleri</td>
                <td className="py-3 px-4">Nishane, Byredo, Le Labo, Maison Francis Kurkdjian, Xerjoff, Amouage, Vilhelm</td>
                <td className="py-3 px-4"><strong>Doğrudan hedef kitle</strong> — niş parfüm alıcısı, yüksek AOV, koleksiyoner profil</td>
              </tr>
              <tr className="border-b border-gray-100">
                <td className="py-3 px-4 font-medium text-gray-900">Lüks Moda & Aksesuar</td>
                <td className="py-3 px-4">Beymen, Vakko, Louis Vuitton, Hermès, Bottega Veneta, Brunello Cucinelli</td>
                <td className="py-3 px-4">Ultra-premium alışveriş alışkanlığı — marka hikayesi ve zanaatkarlığa değer veriyor</td>
              </tr>
              <tr className="border-b border-gray-100">
                <td className="py-3 px-4 font-medium text-gray-900">Premium Kozmetik & Skincare</td>
                <td className="py-3 px-4">La Mer, Aesop, Drunk Elephant, Augustinus Bader, La Prairie, SK-II</td>
                <td className="py-3 px-4">Kendine bakımda premium ürün tercih ediyor — parfüm geçişi doğal</td>
              </tr>
              <tr className="border-b border-gray-100">
                <td className="py-3 px-4 font-medium text-gray-900">Ev Kokusu & Mum</td>
                <td className="py-3 px-4">Diptyque, Cire Trudon, Byredo Home, Jo Malone, Rituals</td>
                <td className="py-3 px-4">Koku kültürüne yatırım yapıyor — mekan ve beden kokusu ayrımı bilen profil</td>
              </tr>
              <tr className="border-b border-gray-100">
                <td className="py-3 px-4 font-medium text-gray-900">Lüks Seyahat & Otel</td>
                <td className="py-3 px-4">Four Seasons, The Edition, Aman, Soho House, Six Senses</td>
                <td className="py-3 px-4">Deneyim odaklı lüks tüketici — duty-free niş parfüm alıcısı potansiyeli</td>
              </tr>
              <tr className="border-b border-gray-100">
                <td className="py-3 px-4 font-medium text-gray-900">Fine Dining & Gastronomi</td>
                <td className="py-3 px-4">Mikla, Neolokal, Turk Fatih Tutak, Nicole, Nusr-Et</td>
                <td className="py-3 px-4">Detaylara dikkat eden, deneyime para harcayan, duyusal zevklere açık profil</td>
              </tr>
              <tr className="border-b border-gray-100">
                <td className="py-3 px-4 font-medium text-gray-900">Sanat & Kültür</td>
                <td className="py-3 px-4">İstanbul Modern, Pera Müzesi, Arter, Contemporary Istanbul, Art Basel</td>
                <td className="py-3 px-4">Estetik duyarlılık yüksek — niş parfümü sanat eseri gibi algılayan kitle</td>
              </tr>
              <tr className="border-b border-gray-100">
                <td className="py-3 px-4 font-medium text-gray-900">Premium Otomotiv</td>
                <td className="py-3 px-4">Porsche, Mercedes AMG, BMW M, Range Rover, Tesla Model S</td>
                <td className="py-3 px-4">Üst gelir grubu teyidi — prestij ve zanaatkarlık odaklı tüketici</td>
              </tr>
              <tr className="border-b border-gray-100">
                <td className="py-3 px-4 font-medium text-gray-900">Saat & Mücevher</td>
                <td className="py-3 px-4">Rolex, Omega, IWC, Cartier, Piaget, Atasay Premium</td>
                <td className="py-3 px-4">Koleksiyoner ruhu, kalıcı değer arayan — parfüm koleksiyonu paralel yürür</td>
              </tr>
              <tr className="border-b border-gray-100">
                <td className="py-3 px-4 font-medium text-gray-900">Wellness & Spa</td>
                <td className="py-3 px-4">Çemberlitaş Hamamı, Kılıç Ali Paşa Hamamı, Espa, Clinique La Prairie</td>
                <td className="py-3 px-4">Hamam/arınma ritüeli ilgisi — Turkish Bath Rituals koleksiyonuna doğrudan bağ</td>
              </tr>
              <tr>
                <td className="py-3 px-4 font-medium text-gray-900">Mainstream'den Niş'e Geçiş</td>
                <td className="py-3 px-4">Chanel, Dior Privé, Tom Ford Private Blend, YSL, Guerlain</td>
                <td className="py-3 px-4"><strong>Upgrade potansiyeli</strong> — designer'dan niş'e yükseliş eğilimindeki segment</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* ───────── Buyer Personas ───────── */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-bold text-gray-900">Buyer Personas</h2>
        </div>
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">

          {/* Persona 1 */}
          <div className="rounded-xl border overflow-hidden">
            <div className="bg-[#111] px-5 py-4 text-white">
              <h3 className="font-bold text-base">Koleksiyoner Kaan</h3>
              <p className="text-neutral-400 text-xs mt-0.5">38 · Girişimci · İstanbul</p>
            </div>
            <div className="p-5 space-y-3 text-sm text-gray-700">
              <p>50+ şişelik niş parfüm koleksiyonu var. Fragrantica ve Parfumo&apos;da aktif. Her yeni çıkan niş markayı dekant ile dener, beğenirse tam şişe alır.</p>
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Motivasyon</p>
                <p>Nadir bileşenler, hikaye derinliği, şişe tasarımı. &quot;Bunu kimse bilmiyor&quot; hissi çok değerli.</p>
              </div>
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Yaşam Tarzı Profili</p>
                <div className="flex flex-wrap gap-1.5">
                  {['Nishane koleksiyonu', 'Parfumo 8.5+ puanlılar', 'İstinyePark Beymen', 'Porsche Taycan', 'Soho House üyelik', 'Art Basel ziyareti', 'Dekant takas grupları', 'Vintage Guerlain avcısı'].map(t => <span key={t} className="px-2 py-0.5 text-[10px] font-medium bg-amber-50 text-amber-700 rounded-full">{t}</span>)}
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {['Nishane', 'Xerjoff', 'Amouage', 'MFK'].map(b => <span key={b} className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-800 rounded">{b}</span>)}
              </div>
              <p className="text-xs text-gray-500">Fragrantica · Parfumo · YouTube reviews · Reddit r/fragrance</p>
            </div>
          </div>

          {/* Persona 2 */}
          <div className="rounded-xl border overflow-hidden">
            <div className="bg-[#111] px-5 py-4 text-white">
              <h3 className="font-bold text-base">Signature Scent Selin</h3>
              <p className="text-neutral-400 text-xs mt-0.5">34 · Avukat · İstanbul</p>
            </div>
            <div className="p-5 space-y-3 text-sm text-gray-700">
              <p>Tek bir imza kokusu arıyor — &quot;seni koklayınca akla gelen&quot; parfüm. Mainstream&apos;den sıkıldı, niş&apos;e geçiş yapıyor. Deneyim ve hikaye önemli.</p>
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Motivasyon</p>
                <p>Kimlik ifadesi, exclusivity. &quot;Chanel No.5 değil, kimsenin tanımadığı ama herkesin sorduğu bir koku&quot; istiyor.</p>
              </div>
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Yaşam Tarzı Profili</p>
                <div className="flex flex-wrap gap-1.5">
                  {['Beymen alışverişi', 'Nişantaşı brunch', 'Aesop el kremi', 'La Mer cilt bakımı', 'Vogue Türkiye aboneliği', 'Bodrum yazlık', 'Pilates reformer', 'Contemporary İstanbul VIP'].map(t => <span key={t} className="px-2 py-0.5 text-[10px] font-medium bg-pink-50 text-pink-700 rounded-full">{t}</span>)}
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {['Byredo', 'Le Labo', 'Diptyque', 'Jo Malone'].map(b => <span key={b} className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-800 rounded">{b}</span>)}
              </div>
              <p className="text-xs text-gray-500">Instagram Keşfet · Pinterest · TikTok #perfumetok · Google Search</p>
            </div>
          </div>

          {/* Persona 3 */}
          <div className="rounded-xl border overflow-hidden">
            <div className="bg-[#111] px-5 py-4 text-white">
              <h3 className="font-bold text-base">Hediye Arayan Mehmet</h3>
              <p className="text-neutral-400 text-xs mt-0.5">45 · Üst Düzey Yönetici · Ankara</p>
            </div>
            <div className="p-5 space-y-3 text-sm text-gray-700">
              <p>Eşine, iş ortağına veya VIP müşterisine özel bir hediye arıyor. Parfüm bilgisi sınırlı ama &quot;farklı ve prestijli&quot; bir şey istiyor. Paketleme çok önemli.</p>
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Motivasyon</p>
                <p>Prestij, şaşırtma etkisi, hediye kutusu deneyimi. Hızlı teslimat ve güzel sunum kritik.</p>
              </div>
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Yaşam Tarzı Profili</p>
                <div className="flex flex-wrap gap-1.5">
                  {['Vakko hediye kartı', 'Mercedes S-Serisi', 'Turkcell Platinum', 'Zorlu Center', 'Yılda 5+ iş seyahati', 'Golf kulübü', 'Four Seasons toplantılar', 'Rolex Datejust'].map(t => <span key={t} className="px-2 py-0.5 text-[10px] font-medium bg-gray-100 text-gray-600 rounded-full">{t}</span>)}
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {['Tom Ford', 'Creed', 'Hermès'].map(b => <span key={b} className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-800 rounded">{b}</span>)}
              </div>
              <p className="text-xs text-gray-500">Google Search · Google Shopping · Instagram reklamlar</p>
            </div>
          </div>

          {/* Persona 4 */}
          <div className="rounded-xl border overflow-hidden">
            <div className="bg-[#111] px-5 py-4 text-white">
              <h3 className="font-bold text-base">Kültür Tutkunu Defne</h3>
              <p className="text-neutral-400 text-xs mt-0.5">42 · İç Mimar · İzmir</p>
            </div>
            <div className="p-5 space-y-3 text-sm text-gray-700">
              <p>Osmanlı kültürü, Türk hamam ritüelleri ve Anadolu geleneklerine ilgi duyuyor. Régalien&apos;in Heritage ve Turkish Bath Rituals koleksiyonlarını hikayesi yüzünden seviyor.</p>
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Motivasyon</p>
                <p>Kültürel bağ, yerli üretim gururu. &quot;Türk niş parfümü dünyaya açılıyor&quot; hikayesinin parçası olmak istiyor.</p>
              </div>
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Yaşam Tarzı Profili</p>
                <div className="flex flex-wrap gap-1.5">
                  {['Pera Müzesi üyelik', 'Çemberlitaş Hamamı', 'Kilim koleksiyonu', 'Türk kahvesi ritüeli', 'Bodrum taş ev', 'Elle Decor abonelik', 'Antika pazarı', 'Yerel zanaatkar destekçisi'].map(t => <span key={t} className="px-2 py-0.5 text-[10px] font-medium bg-emerald-50 text-emerald-700 rounded-full">{t}</span>)}
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {['Nishane', 'Régalien', 'Atelier Rebul'].map(b => <span key={b} className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-800 rounded">{b}</span>)}
              </div>
              <p className="text-xs text-gray-500">Instagram · Kültür blogları · Sanat etkinlikleri · Pinterest</p>
            </div>
          </div>

          {/* Persona 5 */}
          <div className="rounded-xl border overflow-hidden">
            <div className="bg-[#111] px-5 py-4 text-white">
              <h3 className="font-bold text-base">TikTok Keşifçisi Ece</h3>
              <p className="text-neutral-400 text-xs mt-0.5">26 · Marka Yöneticisi · İstanbul</p>
            </div>
            <div className="p-5 space-y-3 text-sm text-gray-700">
              <p>#PerfumeTok&apos;tan niş parfüm dünyasını keşfetti. İlk niş parfümünü almak istiyor ama fiyatlar yüksek — discovery set ile başlayacak, beğenirse tam şişe alacak.</p>
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Motivasyon</p>
                <p>&quot;İlk niş parfümüm&quot; heyecanı. Sosyal medyada paylaşılabilir estetik, unboxing deneyimi, akran onayı.</p>
              </div>
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Yaşam Tarzı Profili</p>
                <div className="flex flex-wrap gap-1.5">
                  {['Sephora Gold Card', 'Karaköy kafeler', 'Zara / COS ofis', 'TikTok #GRWM', 'Spotify Chill playlist', 'Glossier skincare', 'Pinterest moodboard', 'Airbnb tatil'].map(t => <span key={t} className="px-2 py-0.5 text-[10px] font-medium bg-violet-50 text-violet-700 rounded-full">{t}</span>)}
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {['Jo Malone', 'Maison Margiela', 'Glossier You'].map(b => <span key={b} className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-800 rounded">{b}</span>)}
              </div>
              <p className="text-xs text-gray-500">TikTok · Instagram Reels · YouTube Shorts · Google Search</p>
            </div>
          </div>

          {/* Persona 6 */}
          <div className="rounded-xl border overflow-hidden">
            <div className="bg-[#111] px-5 py-4 text-white">
              <h3 className="font-bold text-base">Expat &amp; Turist James</h3>
              <p className="text-neutral-400 text-xs mt-0.5">40 · Danışman · İstanbul (Expat) / Turist</p>
            </div>
            <div className="p-5 space-y-3 text-sm text-gray-700">
              <p>İstanbul&apos;da yaşayan yabancı veya tatile gelen turist. Türkiye&apos;ye özgü, otantik ve kaliteli bir hatıra/hediye arıyor. Niş parfüm zaten ilgi alanı.</p>
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Motivasyon</p>
                <p>Otantiklik, &quot;Made in Istanbul&quot; hikayesi, Türk kültürünü temsil eden lüks ürün. Duty-free/butik keşfi.</p>
              </div>
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Yaşam Tarzı Profili</p>
                <div className="flex flex-wrap gap-1.5">
                  {['Grand Bazaar turu', 'Soho House İstanbul', 'Boutique otel', 'Michelin restoran', 'Instagram travel', 'Hamam deneyimi', 'Türk kahvesi seti', 'Kapalıçarşı alışveriş'].map(t => <span key={t} className="px-2 py-0.5 text-[10px] font-medium bg-teal-50 text-teal-700 rounded-full">{t}</span>)}
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {['Penhaligon\'s', 'Frédéric Malle', 'Serge Lutens'].map(b => <span key={b} className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-800 rounded">{b}</span>)}
              </div>
              <p className="text-xs text-gray-500">Google Maps · TripAdvisor · Instagram · Niche perfume blogs</p>
            </div>
          </div>

        </div>
      </div>

      {/* ───────── SWOT Analysis ───────── */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-bold text-gray-900">SWOT Analizi</h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-gray-200 rounded-xl overflow-hidden border border-gray-200">

            {/* Strengths */}
            <div className="bg-white p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-gray-900">Güçlü Yanlar</h3>
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">S</span>
              </div>
              <ul className="space-y-3">
                {[
                  ['Güçlü Kültürel Hikaye', 'Osmanlı mirası, Türk hamam ritüelleri, Doğu-Batı sentezi — rakiplerin kopyalayamayacağı otantik bir marka DNA\'sı. "Krallığa ait" isim, her şişede kültürel derinlik.'],
                  ['Extrait de Parfum Konsantrasyonu', 'Tüm ürünler en yoğun formda üretiliyor — kalıcılık ve sillage konusunda doğal üstünlük. Niş parfüm meraklılarının aradığı premium deneyim.'],
                  ['Uluslararası Tanınırlık', 'Parfumo\'da 36 ürün, 8+ puan alan kokular (Châtelain 22: 8.7, Dem: 8.4). Avrupa niş butiklerinde satış (İtalya, vb.). Global niş parfüm haritasında yer edinmiş.'],
                  ['4 Farklı Koleksiyon Derinliği', 'Exclusive, Heritage, Lucky, Turkish Bath Rituals — farklı duygu ve hikaye eksenleri. Her koleksiyon farklı bir persona ve kullanım anına hitap eder.'],
                  ['Türk Markası Avantajı', 'Yerli üretim, TL bazlı maliyet yapısı, EUR/USD fiyatlama. Avrupa niş markalarına göre fiyat/kalite avantajı — özellikle export pazarında cazip konumlandırma.'],
                  ['Yüksek AOV & Marj', 'Ortalama sipariş ₺8.500+ — düşük adetli satışta bile yüksek gelir. Parfüm sektöründe marj oranları %60-80 aralığında, sürdürülebilir büyüme potansiyeli.'],
                ].map(([title, desc], i) => (
                  <li key={i}>
                    <p className="text-sm font-semibold text-gray-900">{title}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{desc}</p>
                  </li>
                ))}
              </ul>
            </div>

            {/* Weaknesses */}
            <div className="bg-white p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-gray-900">Zayıf Yanlar</h3>
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">W</span>
              </div>
              <ul className="space-y-3">
                {[
                  ['Düşük Marka Bilinirliği (Yurt İçi)', '"Niş parfüm" denince Nishane akla gelirken Régalien henüz ikinci sırada değil. Unaided brand awareness düşük — özellikle niş\'e yeni geçen segment Régalien\'i tanımıyor.'],
                  ['₺8.500+ Fiyat Bariyeri', 'Türkiye\'de ortalama parfüm bütçesi ₺300-800 aralığındayken ₺8.500+ çok yüksek. Potansiyel müşterinin ilk tepkisi "çok pahalı" olabilir — değer algısı oluşturma zorunlu.'],
                  ['Koklamadan Alamama Sorunu', 'Parfüm en kişisel ürünlerden biri — online satışta "ya beğenmezsem" bariyeri çok güçlü. Discovery set çözüm ama awareness ve güven gerekiyor.'],
                  ['Sınırlı Fiziksel Temas Noktası', 'Kendi butik mağazası yok. Bayiler sınırlı (Nokta Cosmetics, Dekant Parfüm). Sephora/Beymen gibi büyük perakendede yer almıyor — keşfedilebilirlik düşük.'],
                  ['WooCommerce Altyapı Limitleri', 'E-ticaret sitesi WordPress/WooCommerce üzerinde — ölçeklenebilirlik, site hızı, checkout deneyimi ve CRM entegrasyonları premium markalar için sınırlayıcı olabilir.'],
                  ['İçerik & SEO Birikimi Yetersiz', 'Blog, parfüm rehberleri, ingredient hikayeleri gibi organik içerik kütüphanesi henüz zayıf — Nishane ve global rakiplerin SEO üstünlüğüne karşı dezavantaj.'],
                  ['Tek Kanal Bağımlılığı', 'Online satışa ağırlıklı bağımlılık — fiziksel retail, department store ve travel retail kanalları henüz gelişmemiş.'],
                ].map(([title, desc], i) => (
                  <li key={i}>
                    <p className="text-sm font-semibold text-gray-900">{title}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{desc}</p>
                  </li>
                ))}
              </ul>
            </div>

            {/* Opportunities */}
            <div className="bg-white p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-gray-900">Fırsatlar</h3>
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">O</span>
              </div>
              <ul className="space-y-3">
                {[
                  ['#PerfumeTok & Niş Parfüm Trendi', 'TikTok ve Instagram\'da niş parfüm içerikleri patlama yaşıyor. "Niche fragrance" aramaları yılda %40+ büyüyor. Régalien bu dalganın Türk temsilcisi olabilir.'],
                  ['Türk Niş Parfüm Markası Gururu', 'Nishane\'nin global başarısı Türk niş parfüm kategorisini meşrulaştırdı. "İkinci Nishane" arayışı var — Régalien bu pozisyona en yakın aday.'],
                  ['Export & Travel Retail Potansiyeli', 'EUR fiyatlama ile Avrupa niş butiklerine giriş avantajı. Dubai, Londra, Paris duty-free ve concept store\'lar — yüksek marjlı global pazar.'],
                  ['Discovery Set & Dekant Stratejisi', 'Yüksek fiyat bariyerini aşmanın en etkili yolu. ₺500-800 arası giriş ürünü → tam şişe dönüşümü. Funnel\'ın tepesini genişletme fırsatı.'],
                  ['Hediye Ekonomisi', 'Niş parfüm, lüks hediye segmentinde hızla büyüyor. Sevgililer Günü, yılbaşı, kurumsal hediye — yılın belirli dönemlerinde ciro %200-300 artış potansiyeli.'],
                  ['Influencer & Parfümör İşbirlikleri', 'Niş parfüm YouTuber\'ları (Demi Rawling, Jeremy Fragrance TR muadilleri) ile işbirliği — düşük maliyetle yüksek güvenilirlik ve hedef kitleye doğrudan ulaşım.'],
                  ['Kültürel Turizm Bağlantısı', 'İstanbul\'u ziyaret eden 15M+ turist — "Made in Istanbul" otantik lüks ürün arayışı. Kapalıçarşı, butik otel, hamam deneyimi ile entegre satış fırsatı.'],
                  ['Abonelik & Refill Modeli', 'Sadık müşteriye aylık dekant aboneliği veya refill seçeneği — CLV artışı ve sürdürülebilirlik mesajı birlikte.'],
                ].map(([title, desc], i) => (
                  <li key={i}>
                    <p className="text-sm font-semibold text-gray-900">{title}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{desc}</p>
                  </li>
                ))}
              </ul>
            </div>

            {/* Threats */}
            <div className="bg-white p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-gray-900">Tehditler</h3>
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">T</span>
              </div>
              <ul className="space-y-3">
                {[
                  ['Nishane\'nin Dominantlığı', 'Nishane, Türk niş parfüm kategorisinde açık ara lider — global dağıtım, yüksek bilinirlik, geniş portföy. Régalien\'in "Türk niş parfüm" denince ikinci akla gelen olması bile zor.'],
                  ['Ekonomik Baskı & TL Değer Kaybı', 'Enflasyon ortamında ₺8.500+ parfüm ultra-lüks kategorisine kayıyor. Orta-üst segment tüketici "designer yeter" diyerek niş\'e geçişi erteleyebilir.'],
                  ['Sahte Ürün & Dekant Piyasası', 'Türkiye\'de kontrolsüz dekant satışı ve muadil parfüm pazarı çok büyük. "Aynı koku 200 TL\'ye var" algısı, niş parfümün değer algısını aşındırabilir.'],
                  ['Global Niş Markaların Türkiye Girişi', 'Byredo, Le Labo, MFK gibi markalar Beymen/Sephora aracılığıyla Türkiye pazarına giriyor — doğrudan aynı müşteri havuzunu hedefliyor.'],
                  ['Koku Deneyimi Olmadan Satış Zorluğu', 'Online parfüm satışında conversion rate sektör ortalaması %1-2 — fiziksel deneyim olmadan lüks parfüm satmak en büyük yapısal zorluk.'],
                ].map(([title, desc], i) => (
                  <li key={i}>
                    <p className="text-sm font-semibold text-gray-900">{title}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{desc}</p>
                  </li>
                ))}
              </ul>
            </div>

          </div>
        </div>
      </div>

      {/* ───────── Rekabet Haritası ───────── */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-bold text-gray-900">Rekabet Haritası</h2>
        </div>
        <div className="p-6">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr className="border-b-2 border-gray-900">
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-900 uppercase tracking-wider">Rakip</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-900 uppercase tracking-wider">Segment</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-900 uppercase tracking-wider">Güçlü Yanı</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-900 uppercase tracking-wider">Zayıf Yanı</th>
                  <th className="px-4 py-3 text-center text-xs font-bold text-gray-900 uppercase tracking-wider">Tehdit</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {[
                  { name: 'Nishane', segment: 'Niş parfüm (Türk)', strong: 'Global dağıtım, yüksek bilinirlik, 100+ parfüm portföyü, Fragrantica yıldızı', weak: 'Çok geniş portföy — koleksiyon hikayesi zayıflayabiliyor', threat: 'Yüksek' },
                  { name: 'Byredo', segment: 'Niş / Lüks lifestyle', strong: 'Güçlü marka kimliği, Beymen\'de satış, minimalist estetik', weak: 'Türk kültürel bağı yok, fiyat çok yüksek (€170-300)', threat: 'Yüksek' },
                  { name: 'Le Labo', segment: 'Niş artisan', strong: 'Kişiselleştirme deneyimi, kült marka statüsü', weak: 'Türkiye\'de sınırlı erişim, çok spesifik estetik', threat: 'Orta' },
                  { name: 'Atelier Rebul', segment: 'Türk premium', strong: 'Fiziksel mağaza ağı, hamam konsepti, hediye segmenti', weak: 'Parfüm derinliği sığ — niş parfüm meraklısına yetersiz', threat: 'Orta' },
                  { name: 'Xerjoff', segment: 'Ultra-lüks niş', strong: 'Şişe tasarımı, İtalyan zanaatkarlığı, koleksiyoner hedef kitle', weak: 'Çok yüksek fiyat (€300+), Türkiye\'de sınırlı bilinirlik', threat: 'Düşük' },
                  { name: 'Amouage', segment: 'Lüks oryantal', strong: 'Oryantal notalarda otorite, Körfez pazarında güçlü', weak: 'Türk kültürel bağı yok, dijital pazarlama zayıf', threat: 'Düşük' },
                ].map((comp, i) => (
                  <tr key={i}>
                    <td className="px-4 py-3 text-sm font-bold text-gray-900">{comp.name}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{comp.segment}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{comp.strong}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{comp.weak}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex px-2 py-0.5 text-xs font-bold rounded ${
                        comp.threat === 'Yüksek' ? 'bg-gray-900 text-white' :
                        comp.threat === 'Orta' ? 'bg-gray-200 text-gray-800' :
                        'bg-gray-100 text-gray-500'
                      }`}>{comp.threat}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ───────── Keyword Çalışması ───────── */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-bold text-gray-900">Keyword Çalışması</h2>
          <p className="text-xs text-gray-400 mt-1">Google Ads & SEO hedefleme için niş parfüm sektörü anahtar kelime haritası</p>
        </div>
        <div className="p-6 space-y-8">

          {/* Generic Keywords */}
          <div>
            <div className="mb-3">
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Generic Keywords</h3>
              <p className="text-xs text-gray-400 mt-0.5">Marka içermeyen, parfüm kategorisi genel arama terimleri — yüksek hacim, yüksek rekabet</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-gray-900">
                    <th className="text-left py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Keyword</th>
                    <th className="text-left py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Kategori</th>
                    <th className="text-center py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Tahmini Hacim</th>
                    <th className="text-center py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Intent</th>
                  </tr>
                </thead>
                <tbody className="text-gray-700 divide-y divide-gray-100">
                  {[
                    ['niş parfüm', 'Genel Niş', 'Yüksek', 'Araştırma'],
                    ['erkek parfüm önerisi', 'Erkek', 'Çok Yüksek', 'Ticari'],
                    ['kadın parfüm önerisi', 'Kadın', 'Çok Yüksek', 'Ticari'],
                    ['kalıcı parfüm', 'Genel', 'Çok Yüksek', 'Ticari'],
                    ['en iyi niş parfüm 2026', 'Niş', 'Yüksek', 'Araştırma'],
                    ['oud parfüm', 'Oryantal', 'Yüksek', 'Ticari'],
                    ['extrait de parfum', 'Niş', 'Orta', 'Araştırma'],
                    ['türk parfüm markası', 'Yerli', 'Orta', 'Araştırma'],
                    ['hediye parfüm', 'Hediye', 'Çok Yüksek', 'Ticari'],
                    ['lüks parfüm', 'Lüks', 'Yüksek', 'Ticari'],
                    ['amber parfüm', 'Oryantal', 'Orta', 'Ticari'],
                    ['misk parfüm', 'Oryantal', 'Orta', 'Ticari'],
                    ['unisex parfüm', 'Unisex', 'Yüksek', 'Ticari'],
                    ['parfüm seti hediye', 'Hediye', 'Yüksek', 'Ticari'],
                    ['discovery set parfüm', 'Giriş', 'Orta', 'Ticari'],
                    ['parfüm koleksiyonu', 'Koleksiyon', 'Orta', 'Bilgi'],
                    ['hamam kokusu', 'Kültürel', 'Düşük', 'Araştırma'],
                    ['en kalıcı erkek parfüm', 'Erkek', 'Yüksek', 'Araştırma'],
                    ['niche fragrance Turkey', 'Export', 'Düşük', 'Araştırma'],
                    ['parfüm ne demek notaları', 'Eğitim', 'Orta', 'Bilgi'],
                  ].map(([kw, cat, vol, intent], i) => (
                    <tr key={i}>
                      <td className="py-2.5 px-4 font-medium text-gray-900">{kw}</td>
                      <td className="py-2.5 px-4 text-gray-500">{cat}</td>
                      <td className="py-2.5 px-4 text-center">
                        <span className={`inline-flex px-2 py-0.5 text-[10px] font-bold rounded ${
                          vol === 'Çok Yüksek' ? 'bg-gray-900 text-white' :
                          vol === 'Yüksek' ? 'bg-gray-700 text-white' :
                          vol === 'Orta' ? 'bg-gray-200 text-gray-700' :
                          'bg-gray-100 text-gray-400'
                        }`}>{vol}</span>
                      </td>
                      <td className="py-2.5 px-4 text-center text-xs text-gray-500">{intent}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Brand + Generic Keywords */}
          <div>
            <div className="mb-3">
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Brand + Generic Keywords</h3>
              <p className="text-xs text-gray-400 mt-0.5">Régalien marka adı ve koleksiyon isimleriyle aranan terimler — yüksek satın alma niyeti</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-gray-900">
                    <th className="text-left py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Keyword</th>
                    <th className="text-left py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Marka</th>
                    <th className="text-center py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Tahmini Hacim</th>
                    <th className="text-center py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Intent</th>
                  </tr>
                </thead>
                <tbody className="text-gray-700 divide-y divide-gray-100">
                  {[
                    ['regalien parfüm', 'Régalien', 'Orta', 'Ticari'],
                    ['regalien şah', 'Heritage', 'Düşük', 'Ticari'],
                    ['regalien turkuaz', 'Heritage', 'Düşük', 'Ticari'],
                    ['regalien hatır', 'Heritage', 'Düşük', 'Ticari'],
                    ['regalien dem', 'Bath Rituals', 'Düşük', 'Ticari'],
                    ['regalien hammam', 'Bath Rituals', 'Düşük', 'Ticari'],
                    ['regalien ceremony', 'Exclusive', 'Düşük', 'Ticari'],
                    ['regalien hazz', 'Exclusive', 'Düşük', 'Ticari'],
                    ['regalien kashmeran', 'Exclusive', 'Düşük', 'Ticari'],
                    ['regalien chatelain 22', 'Exclusive', 'Düşük', 'Ticari'],
                    ['regalien diamond of velvet', 'Lucky', 'Düşük', 'Ticari'],
                    ['regalien heart of rose', 'Lucky', 'Düşük', 'Ticari'],
                    ['regalien fiyat', 'Régalien', 'Orta', 'Araştırma'],
                    ['regalien parfüm yorum', 'Régalien', 'Düşük', 'Araştırma'],
                    ['regalien nereden alınır', 'Régalien', 'Düşük', 'Ticari'],
                    ['regalien discovery set', 'Régalien', 'Düşük', 'Ticari'],
                  ].map(([kw, brand, vol, intent], i) => (
                    <tr key={i}>
                      <td className="py-2.5 px-4 font-medium text-gray-900">{kw}</td>
                      <td className="py-2.5 px-4 text-gray-500">{brand}</td>
                      <td className="py-2.5 px-4 text-center">
                        <span className={`inline-flex px-2 py-0.5 text-[10px] font-bold rounded ${
                          vol === 'Çok Yüksek' ? 'bg-gray-900 text-white' :
                          vol === 'Yüksek' ? 'bg-gray-700 text-white' :
                          vol === 'Orta' ? 'bg-gray-200 text-gray-700' :
                          'bg-gray-100 text-gray-400'
                        }`}>{vol}</span>
                      </td>
                      <td className="py-2.5 px-4 text-center text-xs text-gray-500">{intent}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Competitor Brand Keywords (Conquest) */}
          <div>
            <div className="mb-3">
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Competitor / Conquest Keywords</h3>
              <p className="text-xs text-gray-400 mt-0.5">Rakip niş parfüm markaları ve designer parfümlerle yapılan aramalar — conquest ve upgrade potansiyeli</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-gray-900">
                    <th className="text-left py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Keyword</th>
                    <th className="text-left py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Hedef Rakip</th>
                    <th className="text-center py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Tahmini Hacim</th>
                    <th className="text-center py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Strateji</th>
                  </tr>
                </thead>
                <tbody className="text-gray-700 divide-y divide-gray-100">
                  {[
                    ['nishane parfüm', 'Nishane', 'Yüksek', 'Conquest'],
                    ['nishane hacivat', 'Nishane', 'Yüksek', 'Conquest'],
                    ['nishane ani', 'Nishane', 'Orta', 'Conquest'],
                    ['byredo parfüm', 'Byredo', 'Orta', 'Conquest'],
                    ['le labo parfüm', 'Le Labo', 'Orta', 'Conquest'],
                    ['xerjoff parfüm', 'Xerjoff', 'Düşük', 'Conquest'],
                    ['amouage parfüm', 'Amouage', 'Düşük', 'Conquest'],
                    ['atelier rebul parfüm', 'Atelier Rebul', 'Orta', 'Conquest'],
                    ['tom ford parfüm', 'Tom Ford', 'Çok Yüksek', 'Upgrade'],
                    ['dior privé', 'Dior', 'Yüksek', 'Upgrade'],
                    ['chanel parfüm erkek', 'Chanel', 'Çok Yüksek', 'Upgrade'],
                    ['creed aventus', 'Creed', 'Yüksek', 'Upgrade'],
                  ].map(([kw, target, vol, strategy], i) => (
                    <tr key={i}>
                      <td className="py-2.5 px-4 font-medium text-gray-900">{kw}</td>
                      <td className="py-2.5 px-4 text-gray-500">{target}</td>
                      <td className="py-2.5 px-4 text-center">
                        <span className={`inline-flex px-2 py-0.5 text-[10px] font-bold rounded ${
                          vol === 'Çok Yüksek' ? 'bg-gray-900 text-white' :
                          vol === 'Yüksek' ? 'bg-gray-700 text-white' :
                          vol === 'Orta' ? 'bg-gray-200 text-gray-700' :
                          'bg-gray-100 text-gray-400'
                        }`}>{vol}</span>
                      </td>
                      <td className="py-2.5 px-4 text-center">
                        <span className={`inline-flex px-2 py-0.5 text-[10px] font-bold rounded ${
                          strategy === 'Conquest' ? 'bg-gray-900 text-white' : 'bg-gray-200 text-gray-700'
                        }`}>{strategy}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Comparison & Research Keywords */}
          <div>
            <div className="mb-3">
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Karşılaştırma & Araştırma Keywords</h3>
              <p className="text-xs text-gray-400 mt-0.5">Satın alma öncesi araştırma yapan kullanıcılar — blog/SEO içerik fırsatı + Search Ads</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-gray-900">
                    <th className="text-left py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Keyword</th>
                    <th className="text-left py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Tip</th>
                    <th className="text-center py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Tahmini Hacim</th>
                    <th className="text-center py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">İçerik Fırsatı</th>
                  </tr>
                </thead>
                <tbody className="text-gray-700 divide-y divide-gray-100">
                  {[
                    ['nishane vs regalien', 'Karşılaştırma', 'Düşük', 'Blog + Video'],
                    ['en iyi türk niş parfüm', 'Listicle', 'Orta', 'Blog + SEO'],
                    ['en iyi oud parfüm 2026', 'Listicle', 'Orta', 'Blog + SEO'],
                    ['byredo vs le labo', 'Karşılaştırma', 'Orta', 'Blog'],
                    ['niş parfüm mü designer mı', 'Karşılaştırma', 'Orta', 'Blog + Video'],
                    ['en kalıcı niş parfüm erkek', 'Listicle', 'Yüksek', 'Blog + SEO'],
                    ['extrait de parfum vs edp fark', 'Eğitim', 'Orta', 'Blog + Rehber'],
                    ['parfüm notaları nasıl okunur', 'Eğitim', 'Orta', 'Blog + Rehber'],
                    ['oud nedir parfümde', 'Eğitim', 'Orta', 'Blog'],
                    ['niş parfüme nasıl başlanır', 'Eğitim', 'Düşük', 'Blog + Rehber'],
                    ['hediye parfüm seçimi rehber', 'Eğitim', 'Orta', 'Blog + E-mail'],
                    ['türk hamam kokuları', 'Kültürel', 'Düşük', 'Blog + SEO'],
                    ['parfüm koleksiyonu nasıl saklanır', 'Eğitim', 'Düşük', 'Blog'],
                    ['yaz parfümü önerisi erkek 2026', 'Sezonsal', 'Yüksek', 'Blog + Ürün'],
                    ['kış parfümü ağır kokular', 'Sezonsal', 'Orta', 'Blog + Ürün'],
                  ].map(([kw, type, vol, content], i) => (
                    <tr key={i}>
                      <td className="py-2.5 px-4 font-medium text-gray-900">{kw}</td>
                      <td className="py-2.5 px-4 text-gray-500">{type}</td>
                      <td className="py-2.5 px-4 text-center">
                        <span className={`inline-flex px-2 py-0.5 text-[10px] font-bold rounded ${
                          vol === 'Çok Yüksek' ? 'bg-gray-900 text-white' :
                          vol === 'Yüksek' ? 'bg-gray-700 text-white' :
                          vol === 'Orta' ? 'bg-gray-200 text-gray-700' :
                          'bg-gray-100 text-gray-400'
                        }`}>{vol}</span>
                      </td>
                      <td className="py-2.5 px-4 text-center text-xs text-gray-500">{content}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Long-Tail & Niche Keywords */}
          <div>
            <div className="mb-3">
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Long-Tail & Niş Keywords</h3>
              <p className="text-xs text-gray-400 mt-0.5">Düşük hacimli ama çok yüksek dönüşüm potansiyeli — spesifik ihtiyaç, net satın alma niyeti</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-gray-900">
                    <th className="text-left py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Keyword</th>
                    <th className="text-left py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Niş</th>
                    <th className="text-center py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Tahmini Hacim</th>
                    <th className="text-center py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Dönüşüm Pot.</th>
                  </tr>
                </thead>
                <tbody className="text-gray-700 divide-y divide-gray-100">
                  {[
                    ['regalien şah parfüm yorum', 'Review', 'Düşük', 'Çok Yüksek'],
                    ['regalien turkuaz fiyat', 'Fiyat Araştırma', 'Düşük', 'Çok Yüksek'],
                    ['regalien dem nereden alınır', 'Satış Noktası', 'Düşük', 'Çok Yüksek'],
                    ['sevgiliye lüks parfüm hediye erkek', 'Hediye', 'Orta', 'Çok Yüksek'],
                    ['anneler günü niş parfüm', 'Sezonsal Hediye', 'Düşük', 'Çok Yüksek'],
                    ['türk yapımı niş parfüm', 'Yerli', 'Düşük', 'Yüksek'],
                    ['hamam kokulu parfüm', 'Kültürel', 'Düşük', 'Çok Yüksek'],
                    ['amber oud extrait de parfum', 'Niş Spesifik', 'Düşük', 'Yüksek'],
                    ['nishane alternatif türk parfüm', 'Conquest', 'Düşük', 'Çok Yüksek'],
                    ['istanbul niş parfüm butik', 'Lokal', 'Düşük', 'Çok Yüksek'],
                    ['kurumsal hediye parfüm seti', 'Kurumsal', 'Düşük', 'Yüksek'],
                    ['discovery set niş parfüm türkiye', 'Giriş', 'Düşük', 'Yüksek'],
                  ].map(([kw, niche, vol, conversion], i) => (
                    <tr key={i}>
                      <td className="py-2.5 px-4 font-medium text-gray-900">{kw}</td>
                      <td className="py-2.5 px-4 text-gray-500">{niche}</td>
                      <td className="py-2.5 px-4 text-center">
                        <span className={`inline-flex px-2 py-0.5 text-[10px] font-bold rounded ${
                          vol === 'Çok Yüksek' ? 'bg-gray-900 text-white' :
                          vol === 'Yüksek' ? 'bg-gray-700 text-white' :
                          vol === 'Orta' ? 'bg-gray-200 text-gray-700' :
                          'bg-gray-100 text-gray-400'
                        }`}>{vol}</span>
                      </td>
                      <td className="py-2.5 px-4 text-center">
                        <span className={`inline-flex px-2 py-0.5 text-[10px] font-bold rounded ${
                          conversion === 'Çok Yüksek' ? 'bg-gray-900 text-white' :
                          conversion === 'Yüksek' ? 'bg-gray-700 text-white' :
                          'bg-gray-200 text-gray-700'
                        }`}>{conversion}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Dönemsel Yatırım Dağılımı */}
          <div>
            <div className="mb-3">
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Dönemsel Keyword Yatırım Dağılımı</h3>
              <p className="text-xs text-gray-400 mt-0.5">Başlangıçta dönüşümü garanti Brand + Generic ağırlıklı, zamanla Generic ve Conquest'e doğru genişleyen strateji</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-gray-900">
                    <th className="text-left py-3 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Keyword Kategorisi</th>
                    <th className="text-center py-3 px-4 text-[10px] font-bold text-gray-900 uppercase tracking-wider bg-gray-50">Q1 — Temel</th>
                    <th className="text-center py-3 px-4 text-[10px] font-bold text-gray-900 uppercase tracking-wider">Q2 — Büyüme</th>
                    <th className="text-center py-3 px-4 text-[10px] font-bold text-gray-900 uppercase tracking-wider bg-gray-50">Q3 — Genişleme</th>
                    <th className="text-center py-3 px-4 text-[10px] font-bold text-gray-900 uppercase tracking-wider">Q4 — Olgunluk</th>
                    <th className="text-center py-3 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Trend</th>
                  </tr>
                </thead>
                <tbody className="text-gray-700">
                  <tr className="border-b border-gray-100">
                    <td className="py-3 px-4">
                      <p className="font-semibold text-gray-900">Brand + Generic</p>
                      <p className="text-[10px] text-gray-400 mt-0.5">regalien şah, regalien dem, regalien fiyat…</p>
                    </td>
                    <td className="py-3 px-4 text-center bg-gray-50">
                      <span className="text-lg font-black text-gray-900">%60</span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className="text-lg font-black text-gray-900">%45</span>
                    </td>
                    <td className="py-3 px-4 text-center bg-gray-50">
                      <span className="text-lg font-black text-gray-700">%35</span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className="text-lg font-black text-gray-700">%30</span>
                    </td>
                    <td className="py-3 px-4 text-center text-xs text-gray-400">↘ Azalan</td>
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="py-3 px-4">
                      <p className="font-semibold text-gray-900">Generic</p>
                      <p className="text-[10px] text-gray-400 mt-0.5">niş parfüm, oud parfüm, hediye parfüm…</p>
                    </td>
                    <td className="py-3 px-4 text-center bg-gray-50">
                      <span className="text-lg font-black text-gray-700">%20</span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className="text-lg font-black text-gray-900">%28</span>
                    </td>
                    <td className="py-3 px-4 text-center bg-gray-50">
                      <span className="text-lg font-black text-gray-900">%32</span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className="text-lg font-black text-gray-900">%30</span>
                    </td>
                    <td className="py-3 px-4 text-center text-xs text-gray-400">↗ Artan</td>
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="py-3 px-4">
                      <p className="font-semibold text-gray-900">Competitor / Conquest</p>
                      <p className="text-[10px] text-gray-400 mt-0.5">nishane parfüm, byredo parfüm, tom ford parfüm…</p>
                    </td>
                    <td className="py-3 px-4 text-center bg-gray-50">
                      <span className="text-lg font-black text-gray-500">%8</span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className="text-lg font-black text-gray-700">%12</span>
                    </td>
                    <td className="py-3 px-4 text-center bg-gray-50">
                      <span className="text-lg font-black text-gray-900">%15</span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className="text-lg font-black text-gray-900">%18</span>
                    </td>
                    <td className="py-3 px-4 text-center text-xs text-gray-400">↗ Artan</td>
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="py-3 px-4">
                      <p className="font-semibold text-gray-900">Karşılaştırma & Araştırma</p>
                      <p className="text-[10px] text-gray-400 mt-0.5">nishane vs regalien, en iyi oud parfüm 2026…</p>
                    </td>
                    <td className="py-3 px-4 text-center bg-gray-50">
                      <span className="text-lg font-black text-gray-500">%7</span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className="text-lg font-black text-gray-700">%9</span>
                    </td>
                    <td className="py-3 px-4 text-center bg-gray-50">
                      <span className="text-lg font-black text-gray-700">%10</span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className="text-lg font-black text-gray-900">%12</span>
                    </td>
                    <td className="py-3 px-4 text-center text-xs text-gray-400">↗ Artan</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4">
                      <p className="font-semibold text-gray-900">Long-Tail & Niş</p>
                      <p className="text-[10px] text-gray-400 mt-0.5">hamam kokulu parfüm, regalien nereden alınır…</p>
                    </td>
                    <td className="py-3 px-4 text-center bg-gray-50">
                      <span className="text-lg font-black text-gray-500">%5</span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className="text-lg font-black text-gray-500">%6</span>
                    </td>
                    <td className="py-3 px-4 text-center bg-gray-50">
                      <span className="text-lg font-black text-gray-700">%8</span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className="text-lg font-black text-gray-700">%10</span>
                    </td>
                    <td className="py-3 px-4 text-center text-xs text-gray-400">↗ Artan</td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-gray-900">
                    <td className="py-3 px-4 font-bold text-gray-900">Toplam</td>
                    <td className="py-3 px-4 text-center bg-gray-50 font-bold text-gray-900">%100</td>
                    <td className="py-3 px-4 text-center font-bold text-gray-900">%100</td>
                    <td className="py-3 px-4 text-center bg-gray-50 font-bold text-gray-900">%100</td>
                    <td className="py-3 px-4 text-center font-bold text-gray-900">%100</td>
                    <td className="py-3 px-4"></td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Dönem Açıklamaları */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
              <div className="rounded-lg border p-4">
                <p className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-2">Q1 — Temel Oluşturma</p>
                <p className="text-xs text-gray-500 leading-relaxed">Hızlı dönüşüm ve veri toplama. Regalien arayanlar zaten satın almaya yakın — brand keyword'lerle data biriktir, ROAS'ı güvenceye al.</p>
              </div>
              <div className="rounded-lg border p-4">
                <p className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-2">Q2 — Büyüme</p>
                <p className="text-xs text-gray-500 leading-relaxed">Q1 verileriyle "niş parfüm", "oud parfüm" gibi generic'lerde hangi keyword'ler dönüştürüyor gör. Conquest'e giriş yap. Blog/video içerik üretimine başla.</p>
              </div>
              <div className="rounded-lg border p-4">
                <p className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-2">Q3 — Genişleme</p>
                <p className="text-xs text-gray-500 leading-relaxed">Generic ve Brand neredeyse eşit. SEO içerikler indeksleniyor (parfüm rehberleri, not analizi). Conquest agresifleşiyor — Nishane, Byredo arayanları yakalama.</p>
              </div>
              <div className="rounded-lg border p-4">
                <p className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-2">Q4 — Olgunluk</p>
                <p className="text-xs text-gray-500 leading-relaxed">Dengeli portföy. Brand hâlâ güçlü ama generic ve conquest büyümüş. Hediye dönemi (Q4 yılbaşı) agresif push — "hediye parfüm" keyword'leri peak season.</p>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* ───────── Targeting Mindmap ───────── */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-bold text-gray-900">Targeting Modelleri — Mindmap</h2>
          <p className="text-xs text-gray-400 mt-1">Régalien hedef kitlesine ulaşmak için kullanılacak 8 farklı hedefleme yaklaşımı</p>
        </div>
        {/* === CARD GRID MINDMAP === */}
        <div className="p-8">

          {/* Center Hub */}
          <div className="flex flex-col items-center mb-10">
            <div className="bg-[#111] text-white px-8 py-4 rounded-2xl shadow-lg">
              <p className="text-lg font-black tracking-wide text-center">RÉGALIEN TARGETING</p>
              <p className="text-[10px] text-neutral-400 text-center mt-0.5 tracking-widest uppercase">8 Hedefleme Modeli</p>
            </div>
            <div className="w-px h-8 bg-gray-300"></div>
          </div>

          {/* Branches Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">

            {/* 1. Keyword Targeting */}
            <div className="relative">
              <div className="bg-gray-900 text-white px-4 py-3 rounded-t-xl">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-bold uppercase tracking-wider">① Keyword Targeting</p>
                  <span className="text-[9px] bg-white/20 px-1.5 py-0.5 rounded">Google Ads</span>
                </div>
              </div>
              <div className="border border-t-0 rounded-b-xl p-4 space-y-2">
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-900 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">Brand + Generic</p><p className="text-[10px] text-gray-400">regalien şah, regalien dem…</p></div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-700 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">Generic</p><p className="text-[10px] text-gray-400">niş parfüm, oud parfüm, hediye parfüm…</p></div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-500 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">Conquest</p><p className="text-[10px] text-gray-400">nishane parfüm, byredo, tom ford…</p></div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">Karşılaştırma</p><p className="text-[10px] text-gray-400">hoka vs on, en iyi trail…</p></div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-300 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">Long-Tail</p><p className="text-[10px] text-gray-400">hamam kokulu parfüm, regalien nereden alınır…</p></div>
                </div>
              </div>
            </div>

            {/* 2. Interest-Based */}
            <div className="relative">
              <div className="bg-gray-900 text-white px-4 py-3 rounded-t-xl">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-bold uppercase tracking-wider">② Interest Targeting</p>
                  <span className="text-[9px] bg-white/20 px-1.5 py-0.5 rounded">Meta + Google</span>
                </div>
              </div>
              <div className="border border-t-0 rounded-b-xl p-4 space-y-2">
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-900 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">Parfüm & Güzellik</p><p className="text-[10px] text-gray-400">niş parfüm, oud, amber, skincare</p></div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-700 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">Lüks Yaşam Tarzı</p><p className="text-[10px] text-gray-400">Beymen, Vakko, lüks otel, fine dining</p></div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-500 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">Beslenme & Wellness</p><p className="text-[10px] text-gray-400">sağlıklı beslenme, protein, organik</p></div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">Medya & İçerik</p><p className="text-[10px] text-gray-400">parfüm review, fragrance vlog…</p></div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-300 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">Kültür & Sanat</p><p className="text-[10px] text-gray-400">müze, galeri, edebiyat, tarih</p></div>
                </div>
              </div>
            </div>

            {/* 3. Cross-Shopping Intent */}
            <div className="relative">
              <div className="bg-gray-900 text-white px-4 py-3 rounded-t-xl">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-bold uppercase tracking-wider">③ Alışveriş Intent</p>
                  <span className="text-[9px] bg-white/20 px-1.5 py-0.5 rounded">Meta + Google</span>
                </div>
              </div>
              <div className="border border-t-0 rounded-b-xl p-4 space-y-2">
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-900 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">Premium Moda</p><p className="text-[10px] text-gray-400">Beymen, Vakko, Network…</p></div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-700 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">Athleisure & Spor Moda</p><p className="text-[10px] text-gray-400">Lululemon, Arc'teryx, TNF…</p></div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-500 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">Sağlık & Beslenme</p><p className="text-[10px] text-gray-400">Eataly, iHerb, Supplementler…</p></div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">Kozmetik & Bakım</p><p className="text-[10px] text-gray-400">Sephora, Aesop, Kiehl's…</p></div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-300 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">Rakip Parfüm Markaları</p><p className="text-[10px] text-gray-400">Nishane, Byredo, Le Labo…</p></div>
                </div>
              </div>
            </div>

            {/* 4. Location-Based */}
            <div className="relative">
              <div className="bg-gray-900 text-white px-4 py-3 rounded-t-xl">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-bold uppercase tracking-wider">④ Lokasyon Bazlı</p>
                  <span className="text-[9px] bg-white/20 px-1.5 py-0.5 rounded">Meta + Google</span>
                </div>
              </div>
              <div className="border border-t-0 rounded-b-xl p-4 space-y-2">
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-900 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">Premium Lokasyonlar</p><p className="text-[10px] text-gray-400">Nişantaşı, Bebek, Bodrum, butik oteller</p></div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-700 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">Koşu Parkurları</p><p className="text-[10px] text-gray-400">Caddebostan, Bebek, Konyaaltı…</p></div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-500 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">Premium Bölgeler</p><p className="text-[10px] text-gray-400">Maslak, Levent, Bağdat Cd., Alsancak</p></div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">Doğa Parkurları</p><p className="text-[10px] text-gray-400">Milli parklar, trail rotaları</p></div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-300 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">AVM Proximity</p><p className="text-[10px] text-gray-400">Premium AVM katları çevresindekiler</p></div>
                </div>
              </div>
            </div>

            {/* 5. Demographic */}
            <div className="relative">
              <div className="bg-gray-900 text-white px-4 py-3 rounded-t-xl">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-bold uppercase tracking-wider">⑤ Demografik</p>
                  <span className="text-[9px] bg-white/20 px-1.5 py-0.5 rounded">Meta + Google</span>
                </div>
              </div>
              <div className="border border-t-0 rounded-b-xl p-4 space-y-2">
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-900 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">Yaş: 25–45</p><p className="text-[10px] text-gray-400">Çekirdek: 28–38 arası</p></div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-700 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">Beyaz Yaka</p><p className="text-[10px] text-gray-400">Yazılım, finans, hukuk, sağlık, kreatif</p></div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-500 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">Gelir: A / B+</p><p className="text-[10px] text-gray-400">Üst-orta ve üst gelir segmenti</p></div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">Eğitim: Üniversite+</p><p className="text-[10px] text-gray-400">Lisans ve üstü eğitim seviyesi</p></div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-300 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">Cinsiyet: K55 / E45</p><p className="text-[10px] text-gray-400">Kadın ağırlıklı ama dengeli</p></div>
                </div>
              </div>
            </div>

            {/* 6. Retargeting */}
            <div className="relative">
              <div className="bg-gray-900 text-white px-4 py-3 rounded-t-xl">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-bold uppercase tracking-wider">⑥ Retargeting</p>
                  <span className="text-[9px] bg-white/20 px-1.5 py-0.5 rounded">Meta + Google</span>
                </div>
              </div>
              <div className="border border-t-0 rounded-b-xl p-4 space-y-2">
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-900 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">Site Ziyaretçileri</p><p className="text-[10px] text-gray-400">Son 7/14/30/60/90 gün segmentleri</p></div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-700 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">Sepet Terk Edenler</p><p className="text-[10px] text-gray-400">Add-to-cart yapıp satın almayan</p></div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-500 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">Ürün Görüntüleyenler</p><p className="text-[10px] text-gray-400">Kategori/ürün sayfası ziyareti</p></div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">Mevcut Müşteriler</p><p className="text-[10px] text-gray-400">Cross-sell, upsell, yeni sezon push</p></div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-300 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">Video İzleyiciler</p><p className="text-[10px] text-gray-400">%25/%50/%75/%95 izleyenlere göre segmentleme</p></div>
                </div>
              </div>
            </div>

            {/* 7. Lookalike / Similar */}
            <div className="relative">
              <div className="bg-gray-900 text-white px-4 py-3 rounded-t-xl">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-bold uppercase tracking-wider">⑦ Lookalike</p>
                  <span className="text-[9px] bg-white/20 px-1.5 py-0.5 rounded">Meta + Google</span>
                </div>
              </div>
              <div className="border border-t-0 rounded-b-xl p-4 space-y-2">
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-900 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">Purchaser LAL %1–3</p><p className="text-[10px] text-gray-400">Satın alan müşteri benzerleri</p></div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-700 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">High-Value LAL</p><p className="text-[10px] text-gray-400">Yüksek AOV müşteri benzerleri</p></div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-500 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">ATC LAL</p><p className="text-[10px] text-gray-400">Add-to-cart yapan benzerleri</p></div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">Website Visitor LAL</p><p className="text-[10px] text-gray-400">Site ziyaretçisi benzerleri</p></div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-300 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">Email List LAL</p><p className="text-[10px] text-gray-400">CRM listesi benzerleri</p></div>
                </div>
              </div>
            </div>

            {/* 8. Contextual & Placement */}
            <div className="relative">
              <div className="bg-gray-900 text-white px-4 py-3 rounded-t-xl">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-bold uppercase tracking-wider">⑧ Contextual</p>
                  <span className="text-[9px] bg-white/20 px-1.5 py-0.5 rounded">Google + YT</span>
                </div>
              </div>
              <div className="border border-t-0 rounded-b-xl p-4 space-y-2">
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-900 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">YouTube Kanalları</p><p className="text-[10px] text-gray-400">Parfüm review, fragrance haul, unboxing</p></div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-700 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">Content Kategorileri</p><p className="text-[10px] text-gray-400">Spor, sağlık, outdoor, wellness</p></div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-500 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">Placement (GDN)</p><p className="text-[10px] text-gray-400">Fragrantica, Vogue, GQ, Kafadar…</p></div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">App Targeting</p><p className="text-[10px] text-gray-400">Sephora, Pinterest, Vogue app</p></div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-300 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">Topic Targeting</p><p className="text-[10px] text-gray-400">Parfüm inceleme, hediye rehberi, lüks yaşam…</p></div>
                </div>
              </div>
            </div>

          </div>

          {/* Summary Bar */}
          <div className="mt-8 rounded-xl bg-gray-50 border p-5">
            <p className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-3">Targeting Katmanları Özet</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="text-center p-3 bg-white rounded-lg border">
                <p className="text-2xl font-black text-gray-900">8</p>
                <p className="text-[10px] text-gray-400 uppercase tracking-wider mt-1">Targeting Modeli</p>
              </div>
              <div className="text-center p-3 bg-white rounded-lg border">
                <p className="text-2xl font-black text-gray-900">40+</p>
                <p className="text-[10px] text-gray-400 uppercase tracking-wider mt-1">Alt Segment</p>
              </div>
              <div className="text-center p-3 bg-white rounded-lg border">
                <p className="text-2xl font-black text-gray-900">2</p>
                <p className="text-[10px] text-gray-400 uppercase tracking-wider mt-1">Ana Platform</p>
                <p className="text-[10px] text-gray-400">Meta + Google</p>
              </div>
              <div className="text-center p-3 bg-white rounded-lg border">
                <p className="text-2xl font-black text-gray-900">3</p>
                <p className="text-[10px] text-gray-400 uppercase tracking-wider mt-1">Funnel Katmanı</p>
                <p className="text-[10px] text-gray-400">TOFU / MOFU / BOFU</p>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* ───────── Kullanıcı Yolculuğu & Funnel Modeli ───────── */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-bold text-gray-900">Kullanıcı Yolculuğu & Harcama Modeli</h2>
          <p className="text-xs text-gray-400 mt-1">New User → Returning User yolculuğunda her adımda hangi kanalda, nasıl, ne kadar harcıyoruz</p>
        </div>
        <div className="p-6">

          {/* Funnel Flow Visual */}
          <div className="flex items-center justify-between mb-8 gap-1 overflow-x-auto">
            {[
              { step: '1', label: 'FARKINDALIK', sub: 'Awareness', width: 'w-full' },
              { step: '2', label: 'MARUZ BIRAKMA', sub: 'Exposure', width: 'w-[95%]' },
              { step: '3', label: 'DEĞERLENDIRME', sub: 'Consideration', width: 'w-[88%]' },
              { step: '4', label: 'KARAR ANI', sub: 'Decision', width: 'w-[78%]' },
              { step: '5', label: 'SATIN ALMA', sub: 'Purchase', width: 'w-[65%]' },
              { step: '6', label: 'TEKRAR HEDEFLEME', sub: 'Re-engagement', width: 'w-[55%]' },
              { step: '7', label: 'SADAKATx REFERANS', sub: 'Loyalty', width: 'w-[45%]' },
            ].map((s, i) => (
              <div key={i} className="flex-1 min-w-[120px]">
                <div className={`bg-gray-900 text-white rounded-lg py-2 px-3 text-center mx-auto ${s.width}`} style={{ opacity: 1 - i * 0.08 }}>
                  <p className="text-[10px] font-bold tracking-widest">{s.step}</p>
                  <p className="text-[9px] font-bold tracking-wider mt-0.5">{s.label}</p>
                  <p className="text-[8px] text-neutral-400">{s.sub}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Main Journey Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-gray-900">
                  <th className="text-left py-3 px-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider w-[13%]">Adım</th>
                  <th className="text-left py-3 px-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider w-[10%]">Kullanıcı Tipi</th>
                  <th className="text-left py-3 px-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider w-[18%]">Kullanıcının Durumu</th>
                  <th className="text-left py-3 px-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider w-[17%]">Kanal & Kampanya Tipi</th>
                  <th className="text-left py-3 px-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider w-[17%]">Targeting Yöntemi</th>
                  <th className="text-center py-3 px-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider w-[8%]">Bütçe Payı</th>
                  <th className="text-left py-3 px-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider w-[10%]">KPI</th>
                  <th className="text-left py-3 px-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider w-[7%]">Hedef</th>
                </tr>
              </thead>
              <tbody className="text-gray-700">

                {/* 1. Farkındalık */}
                <tr className="border-b border-gray-100 bg-gray-50/50">
                  <td className="py-3 px-3" rowSpan="2">
                    <p className="font-bold text-gray-900">1. Farkındalık</p>
                    <p className="text-[10px] text-gray-400 mt-0.5">İlk temas — "Régalien ne?"</p>
                  </td>
                  <td className="py-3 px-3" rowSpan="2">
                    <span className="inline-flex px-2 py-0.5 text-[10px] font-bold rounded bg-gray-900 text-white">NEW USER</span>
                  </td>
                  <td className="py-3 px-3" rowSpan="2">
                    <p className="text-xs text-gray-600">Markayı hiç duymamış. Niş parfüm ilgisi var ama Régalien'i bilmiyor.</p>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs font-medium text-gray-900">Meta — Video Views</p>
                    <p className="text-[10px] text-gray-400">Marka tanıtım videosu, lifestyle içerik</p>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs text-gray-600">Interest + Demografik + Lookalike (Purchaser LAL %3-5)</p>
                  </td>
                  <td className="py-3 px-3 text-center" rowSpan="2">
                    <span className="text-lg font-black text-gray-900">%20</span>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs text-gray-600">CPM, Reach, Video View %50+</p>
                  </td>
                  <td className="py-3 px-3" rowSpan="2">
                    <p className="text-xs text-gray-600">Maksimum erişim</p>
                  </td>
                </tr>
                <tr className="border-b border-gray-200 bg-gray-50/50">
                  <td className="py-3 px-3">
                    <p className="text-xs font-medium text-gray-900">YouTube — Video Ads</p>
                    <p className="text-[10px] text-gray-400">In-stream, bumper, discovery</p>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs text-gray-600">Contextual (spor kanalları) + Custom Intent</p>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs text-gray-600">CPV, View Rate, Brand Lift</p>
                  </td>
                </tr>

                {/* 2. Maruz Bırakma */}
                <tr className="border-b border-gray-100">
                  <td className="py-3 px-3" rowSpan="2">
                    <p className="font-bold text-gray-900">2. Maruz Bırakma</p>
                    <p className="text-[10px] text-gray-400 mt-0.5">Tekrar tekrar göster</p>
                  </td>
                  <td className="py-3 px-3" rowSpan="2">
                    <span className="inline-flex px-2 py-0.5 text-[10px] font-bold rounded bg-gray-900 text-white">NEW USER</span>
                  </td>
                  <td className="py-3 px-3" rowSpan="2">
                    <p className="text-xs text-gray-600">Videoyu gördü ama siteye gelmedi. Markayı duydu, henüz ilgi oluşmadı. Frekans artırılmalı.</p>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs font-medium text-gray-900">Meta — Reach & Frequency</p>
                    <p className="text-[10px] text-gray-400">Carousel, collection, kısa video</p>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs text-gray-600">Video Viewer Custom Audience (%25+) + Interest layering</p>
                  </td>
                  <td className="py-3 px-3 text-center" rowSpan="2">
                    <span className="text-lg font-black text-gray-900">%15</span>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs text-gray-600">Frequency (3-5x), CPM, CTR</p>
                  </td>
                  <td className="py-3 px-3" rowSpan="2">
                    <p className="text-xs text-gray-600">Hatırlama & tanınma</p>
                  </td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="py-3 px-3">
                    <p className="text-xs font-medium text-gray-900">GDN — Display Banners</p>
                    <p className="text-[10px] text-gray-400">Responsive, spor site placement</p>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs text-gray-600">Placement (spor siteleri) + Custom Audience</p>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs text-gray-600">Impression Share, Viewability</p>
                  </td>
                </tr>

                {/* 3. Değerlendirme */}
                <tr className="border-b border-gray-100 bg-gray-50/50">
                  <td className="py-3 px-3" rowSpan="3">
                    <p className="font-bold text-gray-900">3. Değerlendirme</p>
                    <p className="text-[10px] text-gray-400 mt-0.5">Araştırıyor, karşılaştırıyor</p>
                  </td>
                  <td className="py-3 px-3" rowSpan="3">
                    <span className="inline-flex px-2 py-0.5 text-[10px] font-bold rounded bg-gray-700 text-white">WARM USER</span>
                  </td>
                  <td className="py-3 px-3" rowSpan="3">
                    <p className="text-xs text-gray-600">Siteye geldi, ürün baktı, "Nishane vs Régalien" araştırması yapıyor. Fragrantica puanı, review okuyor.</p>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs font-medium text-gray-900">Google — Search Ads</p>
                    <p className="text-[10px] text-gray-400">Brand+Generic, Karşılaştırma KW</p>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs text-gray-600">Keyword: "regalien şah fiyat", "en iyi niş parfüm 2026"</p>
                  </td>
                  <td className="py-3 px-3 text-center" rowSpan="3">
                    <span className="text-lg font-black text-gray-900">%25</span>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs text-gray-600">CPC, CTR, Site Visit</p>
                  </td>
                  <td className="py-3 px-3" rowSpan="3">
                    <p className="text-xs text-gray-600">Siteye trafik çek, listeler oluştur</p>
                  </td>
                </tr>
                <tr className="border-b border-gray-100 bg-gray-50/50">
                  <td className="py-3 px-3">
                    <p className="text-xs font-medium text-gray-900">Google — PMAX</p>
                    <p className="text-[10px] text-gray-400">Ürün feed, shopping, discovery</p>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs text-gray-600">In-market audience + Feed signals</p>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs text-gray-600">CPC, ROAS, Conv. Rate</p>
                  </td>
                </tr>
                <tr className="border-b border-gray-200 bg-gray-50/50">
                  <td className="py-3 px-3">
                    <p className="text-xs font-medium text-gray-900">Meta — Traffic & Engagement</p>
                    <p className="text-[10px] text-gray-400">Blog / inceleme içerik, UGC</p>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs text-gray-600">Alışveriş intent sinyalleri + Lookalike (ATC LAL)</p>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs text-gray-600">CPC, Landing Page View</p>
                  </td>
                </tr>

                {/* 4. Karar Anı */}
                <tr className="border-b border-gray-100">
                  <td className="py-3 px-3" rowSpan="2">
                    <p className="font-bold text-gray-900">4. Karar Anı</p>
                    <p className="text-[10px] text-gray-400 mt-0.5">Sepette, son dürtü lazım</p>
                  </td>
                  <td className="py-3 px-3" rowSpan="2">
                    <span className="inline-flex px-2 py-0.5 text-[10px] font-bold rounded bg-gray-500 text-white">HOT USER</span>
                  </td>
                  <td className="py-3 px-3" rowSpan="2">
                    <p className="text-xs text-gray-600">Ürünü gördü, belki sepete attı ama çıktı. Son push lazım — kargo bedava, stokta azalıyor, yorumlar…</p>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs font-medium text-gray-900">Meta — Retargeting (DPA)</p>
                    <p className="text-[10px] text-gray-400">Dinamik ürün reklamları, carousel</p>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs text-gray-600">Sepet terk + Ürün görüntüleyen (son 7 gün)</p>
                  </td>
                  <td className="py-3 px-3 text-center" rowSpan="2">
                    <span className="text-lg font-black text-gray-900">%20</span>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs text-gray-600">ROAS, CPA, Conv. Rate</p>
                  </td>
                  <td className="py-3 px-3" rowSpan="2">
                    <p className="text-xs text-gray-600">Satın almayı tamamlat</p>
                  </td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="py-3 px-3">
                    <p className="text-xs font-medium text-gray-900">Google — Remarketing + Brand Search</p>
                    <p className="text-[10px] text-gray-400">"regalien" brand araması, RLSA</p>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs text-gray-600">RLSA (site ziyaretçisine search'te tekrar çık) + Brand KW</p>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs text-gray-600">CPA, ROAS, Conversion</p>
                  </td>
                </tr>

                {/* 5. Satın Alma */}
                <tr className="border-b border-gray-100 bg-gray-50/50">
                  <td className="py-3 px-3">
                    <p className="font-bold text-gray-900">5. Satın Alma</p>
                    <p className="text-[10px] text-gray-400 mt-0.5">Tamamlandı, artık müşteri</p>
                  </td>
                  <td className="py-3 px-3">
                    <span className="inline-flex px-2 py-0.5 text-[10px] font-bold rounded bg-gray-900 text-white">CUSTOMER</span>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs text-gray-600">İlk satın alma gerçekleşti. Onay maili, kargo takibi, unboxing deneyimi. Post-purchase journey başlıyor.</p>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs font-medium text-gray-900">CRM — E-mail / SMS</p>
                    <p className="text-[10px] text-gray-400">Teşekkür, bakım rehberi, review isteği</p>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs text-gray-600">Transactional + Post-purchase flow (otomatik)</p>
                  </td>
                  <td className="py-3 px-3 text-center">
                    <span className="text-lg font-black text-gray-500">—</span>
                    <p className="text-[9px] text-gray-400 mt-0.5">Otomasyon</p>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs text-gray-600">NPS, Review Rate, Delivery Speed</p>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs text-gray-600">Memnuniyet & deneyim</p>
                  </td>
                </tr>

                {/* 6. Tekrar Hedefleme */}
                <tr className="border-b border-gray-100">
                  <td className="py-3 px-3" rowSpan="2">
                    <p className="font-bold text-gray-900">6. Tekrar Hedefleme</p>
                    <p className="text-[10px] text-gray-400 mt-0.5">Geri gel, yeni sezon var</p>
                  </td>
                  <td className="py-3 px-3" rowSpan="2">
                    <span className="inline-flex px-2 py-0.5 text-[10px] font-bold rounded bg-gray-200 text-gray-800">RETURNING</span>
                  </td>
                  <td className="py-3 px-3" rowSpan="2">
                    <p className="text-xs text-gray-600">30-90 gün önce aldı, yeni sezon ürünleri çıktı. Ayakkabısı 500 km'ye yaklaştı. Cross-sell fırsatı.</p>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs font-medium text-gray-900">Meta — Retargeting (Cross-sell)</p>
                    <p className="text-[10px] text-gray-400">Yeni sezon, tamamlayıcı ürün</p>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs text-gray-600">Mevcut müşteri listesi (CRM) + Exclude recent purchaser (&lt;14 gün)</p>
                  </td>
                  <td className="py-3 px-3 text-center" rowSpan="2">
                    <span className="text-lg font-black text-gray-900">%12</span>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs text-gray-600">ROAS, Repeat Rate, AOV</p>
                  </td>
                  <td className="py-3 px-3" rowSpan="2">
                    <p className="text-xs text-gray-600">2. satın almayı tetikle</p>
                  </td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="py-3 px-3">
                    <p className="text-xs font-medium text-gray-900">CRM — E-mail / WhatsApp</p>
                    <p className="text-[10px] text-gray-400">Yenileme hatırlatma, özel erişim</p>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs text-gray-600">RFM segmentasyonu — 60-90 gün inaktif tetikleyici</p>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs text-gray-600">Open Rate, CLV, Repeat Purchase</p>
                  </td>
                </tr>

                {/* 7. Sadakat & Referans */}
                <tr className="bg-gray-50/50">
                  <td className="py-3 px-3">
                    <p className="font-bold text-gray-900">7. Sadakat & Referans</p>
                    <p className="text-[10px] text-gray-400 mt-0.5">Elçimiz olsun</p>
                  </td>
                  <td className="py-3 px-3">
                    <span className="inline-flex px-2 py-0.5 text-[10px] font-bold rounded bg-gray-200 text-gray-800">LOYAL</span>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs text-gray-600">3+ kez almış, markayı seviyor. Koşu topluluğuna öneriyor, UGC üretiyor. VIP deneyim sunulmalı.</p>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs font-medium text-gray-900">Community & Loyalty</p>
                    <p className="text-[10px] text-gray-400">Referral program, VIP early access, ambassador</p>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs text-gray-600">CRM VIP segment + High-value purchaser listesi</p>
                  </td>
                  <td className="py-3 px-3 text-center">
                    <span className="text-lg font-black text-gray-900">%8</span>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs text-gray-600">CLV, Referral Rate, UGC Count</p>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs text-gray-600">Marka elçisi yarat</p>
                  </td>
                </tr>

              </tbody>
            </table>
          </div>

          {/* Budget Distribution Summary */}
          <div className="mt-8 grid grid-cols-7 gap-2">
            {[
              { label: 'Farkındalık', pct: '%20', type: 'NEW' },
              { label: 'Maruz Bırakma', pct: '%15', type: 'NEW' },
              { label: 'Değerlendirme', pct: '%25', type: 'WARM' },
              { label: 'Karar Anı', pct: '%20', type: 'HOT' },
              { label: 'Satın Alma', pct: '—', type: 'CRM' },
              { label: 'Tekrar', pct: '%12', type: 'RETURN' },
              { label: 'Sadakat', pct: '%8', type: 'LOYAL' },
            ].map((s, i) => (
              <div key={i} className="text-center p-3 rounded-lg border bg-gray-50">
                <p className="text-xl font-black text-gray-900">{s.pct}</p>
                <p className="text-[9px] font-bold text-gray-500 uppercase tracking-wider mt-1">{s.label}</p>
                <p className="text-[8px] text-gray-400 mt-0.5">{s.type}</p>
              </div>
            ))}
          </div>

          {/* Key Insight Cards */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="rounded-lg border p-4">
              <p className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-2">TOFU — Top of Funnel</p>
              <p className="text-xs text-gray-500 leading-relaxed">Farkındalık + Maruz Bırakma = <strong>%35</strong> bütçe. Yeni kullanıcıyı markayı tanıması, hatırlaması ve güven duyması için hazırlıyoruz. Video, reach, frequency.</p>
            </div>
            <div className="rounded-lg border p-4">
              <p className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-2">MOFU — Mid Funnel</p>
              <p className="text-xs text-gray-500 leading-relaxed">Değerlendirme = <strong>%25</strong> bütçe. En yüksek pay. Search, PMAX, traffic — kullanıcı aktif araştırıyor. Bu adımda siteye çekmek ve pixel'e düşürmek kritik.</p>
            </div>
            <div className="rounded-lg border p-4">
              <p className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-2">BOFU — Bottom of Funnel</p>
              <p className="text-xs text-gray-500 leading-relaxed">Karar + Tekrar + Sadakat = <strong>%40</strong> bütçe. Retargeting, DPA, CRM — zaten ilgilenen veya satın almış kullanıcıyı dönüştür ve geri getir. En yüksek ROAS burada.</p>
            </div>
          </div>

        </div>
      </div>

      {/* ───────── Hedef Placement Listesi ───────── */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-bold text-gray-900">Hedef Placement Listesi — Web Siteleri & YouTube Kanalları</h2>
          <p className="text-xs text-gray-400 mt-1">GDN placement, YouTube kanal hedefleme ve contextual reklam için kullanılacak siteler ve kanallar</p>
        </div>
        <div className="p-6 space-y-8">

          {/* ── YouTube Kanalları ── */}
          <div>
            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-1">YouTube Kanal Hedefleme</h3>
            <p className="text-xs text-gray-400 mb-4">In-stream, bumper ve discovery reklamlarını göstereceğimiz YouTube kanalları ve içerik tipleri</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-gray-900">
                    <th className="text-left py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Kanal / Kanal Tipi</th>
                    <th className="text-left py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Kategori</th>
                    <th className="text-left py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">İçerik Örneği</th>
                    <th className="text-left py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Neden Hedef?</th>
                    <th className="text-center py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Öncelik</th>
                  </tr>
                </thead>
                <tbody className="text-gray-700 divide-y divide-gray-100">
                  {[
                    ['Parfüm Review Kanalları', 'Parfüm', '"En İyi Niş Parfümler 2026", "Régalien Şah İnceleme"', 'Satın alma öncesi araştıran kitleye direkt erişim', 'Çok Yüksek'],
                    ['Demi Rawling, Redolessence, The Fragrance Apprentice', 'Parfüm (Global)', 'Profesyonel niş parfüm analizi, detaylı not piramidi review\'ları', 'Türk izleyicisi büyüyor, niş parfüm meraklısı profili', 'Yüksek'],
                    ['Kişisel Koşu Vlogger\'ları', 'Koşu / Vlog', '"İlk Maratonum", "Günlük 10K Challenge"', 'Micro-influencer etkisi, samimi & güvenilir', 'Çok Yüksek'],
                    ['Lüks Yaşam Tarzı Kanalları', 'Lüks / Lifestyle', '"İstanbul Lüks Alışveriş", "En İyi Hediye Fikirleri"', 'Premium tüketici, hediye arayan kitle', 'Yüksek'],
                    ['Güzellik & Skincare Kanalları', 'Güzellik', 'Skincare rutin, kozmetik haul, self-care videoları', 'Parfüm cross-sell potansiyeli, premium güzellik kitlesi', 'Yüksek'],
                    ['Wellness & Self-Care Kanalları', 'Wellness', 'Aromaterapi, hamam ritüeli, self-care rutini, mindfulness', 'Kadın ağırlıklı, wellness & koku kültürü segmenti', 'Orta'],
                    ['Sağlıklı Beslenme & Meal Prep', 'Beslenme', '"Günlük Makrolarım", "Sporcu Beslenme Planı"', 'Sağlık bilincli + spor yapan profil örtüşmesi', 'Orta'],
                    ['Tech & Wearable Review', 'Teknoloji', 'Garmin inceleme, Apple Watch Ultra, akıllı saat', 'Performans takip eden, yatırım yapan sporcu', 'Yüksek'],
                    ['Türk Kültür & Tarih Kanalları', 'Kültür', 'Osmanlı tarihi, İstanbul gezi, Türk gelenekleri', 'Heritage & Turkish Bath koleksiyonları için kültürel bağ', 'Orta'],
                    ['Hediye & Unboxing Kanalları', 'Hediye', 'Lüks hediye kutu açılımı, premium ürün unboxing', 'Hediye dönemi öncesi push — zamanlama kritik', 'Yüksek'],
                    ['Kültür & Seyahat Vlogger\'ları', 'Seyahat', '"İstanbul Gizli Noktalar", "Türk Hamamı Deneyimi"', 'Kültürel deneyim arayan, Turkish Bath Rituals hedef kitlesi', 'Orta'],
                    ['Motivasyon & Self-Improvement', 'Yaşam Tarzı', '"Sabah Rutini", "5\'te Kalk Koş", "Atomic Habits"', 'Kendine yatırım yapan, disiplinli profil — premium müşteri potansiyeli', 'Orta'],
                  ].map(([name, cat, content, reason, priority], i) => (
                    <tr key={i}>
                      <td className="py-2.5 px-4 font-medium text-gray-900">{name}</td>
                      <td className="py-2.5 px-4 text-gray-500">{cat}</td>
                      <td className="py-2.5 px-4 text-gray-500 text-xs">{content}</td>
                      <td className="py-2.5 px-4 text-gray-600 text-xs">{reason}</td>
                      <td className="py-2.5 px-4 text-center">
                        <span className={`inline-flex px-2 py-0.5 text-[10px] font-bold rounded ${
                          priority === 'Çok Yüksek' ? 'bg-gray-900 text-white' :
                          priority === 'Yüksek' ? 'bg-gray-700 text-white' :
                          'bg-gray-200 text-gray-700'
                        }`}>{priority}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* ── Web Siteleri / GDN Placement ── */}
          <div>
            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-1">Web Sitesi & GDN Placement Hedefleri</h3>
            <p className="text-xs text-gray-400 mb-4">Google Display Network banner reklamları, native reklam ve sponsorlu içerik için hedef siteler</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-gray-900">
                    <th className="text-left py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Site / Site Tipi</th>
                    <th className="text-left py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Kategori</th>
                    <th className="text-left py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Neden Hedef?</th>
                    <th className="text-left py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Reklam Formatı</th>
                    <th className="text-center py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Öncelik</th>
                  </tr>
                </thead>
                <tbody className="text-gray-700 divide-y divide-gray-100">
                  {[
                    ['Fragrantica.com', 'Parfüm Platform', 'Dünyanın en büyük parfüm veritabanı, aktif review topluluğu, satın alma niyeti yüksek', 'Display + Native', 'Çok Yüksek'],
                    ['Garmin Connect / Blog', 'Wearable', 'Garmin kullanıcısı = performans odaklı sporcu, yüksek harcama gücü', 'Display Banner', 'Çok Yüksek'],
                    ['Parfumo.net', 'Parfüm Platform', 'Avrupa odaklı niş parfüm topluluğu, detaylı puanlama sistemi, collector profil', 'Display + Native', 'Çok Yüksek'],
                    ['Vogue Türkiye / Elle Türkiye', 'Moda & Güzellik', 'Premium yaşam tarzı kitlesi, güzellik/parfüm bölümleri, kadın segment ağırlıklı', 'Display + Sponsorlu İçerik', 'Yüksek'],
                    ['GQ Türkiye / Esquire', 'Yaşam Tarzı', 'Premium erkek kitlesi, grooming/koku bölümleri, üst gelir grubu', 'Display + Sponsorlu İçerik', 'Yüksek'],
                    ['Outdoor Rehber / Gezimanya', 'Outdoor / Seyahat', 'Trekking, hiking, doğa yürüyüşü yapan kitle — outdoor ekipman ihtiyacı', 'Display + Native', 'Yüksek'],
                    ['MyFitnessPal / Yazio (Web)', 'Beslenme', 'Kalori/makro takip eden sağlık bilincli kullanıcı, wellness profili', 'Display Banner', 'Orta'],
                    ['AllTrails / Komoot (Web)', 'Rota Planlama', 'Trail ve hiking rotası arayan aktif outdoor kullanıcısı', 'Display Banner', 'Yüksek'],
                    ['Reddit (r/fragrance, r/nichefragrance)', 'Forum / Topluluk', 'Derinlemesine araştırma yapan, review okuyan, koleksiyoner profil', 'Display (Managed Placement)', 'Orta'],
                    ['Ekşi Sözlük — Parfüm Başlıkları', 'Forum / Türkiye', 'Türkiye\'nin en büyük topluluk platformu, "en iyi niş parfüm" başlıkları', 'Display (Managed Placement)', 'Orta'],
                    ['Technopat / DonanimHaber', 'Teknoloji', 'Garmin, akıllı saat, wearable review — tech-savvy sporcu profili', 'Display Banner', 'Orta'],
                    ['Kafadar Dergi', 'Kültür / Sanat', 'Türk kültür, sanat, yaşam tarzı dergisi — Régalien hikayesine uygun okuyucu profili', 'Display + Sponsorlu İçerik', 'Yüksek'],
                    ['Beymen.com / Vakko.com', 'Lüks E-ticaret', 'Lüks alışveriş yapan kitle, güzellik/koku kategorisi sayfaları hedeflenebilir', 'Display (Contextual)', 'Yüksek'],
                    ['Hepsiburada / Trendyol Blog', 'E-ticaret', 'Spor kategorisinde aktif alışveriş yapan kullanıcı, fiyat karşılaştırması', 'Display (Conquest)', 'Yüksek'],
                    ['Sephora.com.tr / Trendyol Kozmetik', 'Kozmetik E-ticaret', 'Parfüm kategorisi ziyaretçileri — upgrade ve conquest potansiyeli', 'Display Banner', 'Yüksek'],
                  ].map(([name, cat, reason, format, priority], i) => (
                    <tr key={i}>
                      <td className="py-2.5 px-4 font-medium text-gray-900">{name}</td>
                      <td className="py-2.5 px-4 text-gray-500">{cat}</td>
                      <td className="py-2.5 px-4 text-gray-600 text-xs">{reason}</td>
                      <td className="py-2.5 px-4 text-gray-500 text-xs">{format}</td>
                      <td className="py-2.5 px-4 text-center">
                        <span className={`inline-flex px-2 py-0.5 text-[10px] font-bold rounded ${
                          priority === 'Çok Yüksek' ? 'bg-gray-900 text-white' :
                          priority === 'Yüksek' ? 'bg-gray-700 text-white' :
                          'bg-gray-200 text-gray-700'
                        }`}>{priority}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* ── Uygulama İçi Reklam Hedefleri ── */}
          <div>
            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-1">Uygulama İçi Reklam Hedefleri</h3>
            <p className="text-xs text-gray-400 mb-4">Mobil uygulamalar içinde gösterilecek reklamlar — kullanıcı en aktif olduğu anda yakalama</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-gray-900">
                    <th className="text-left py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Uygulama</th>
                    <th className="text-left py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Kategori</th>
                    <th className="text-left py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Kullanıcı Profili</th>
                    <th className="text-left py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Reklam Anı</th>
                    <th className="text-center py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Öncelik</th>
                  </tr>
                </thead>
                <tbody className="text-gray-700 divide-y divide-gray-100">
                  {[
                    ['Sephora App', 'Güzellik / Parfüm', 'Premium kozmetik alışverişi yapan, parfüm keşfine açık kitle', 'Güzellik/parfüm kategorisi browsing anı', 'Çok Yüksek'],
                    ['Garmin Connect', 'Wearable', 'Garmin saat sahibi, veri odaklı, premium profil', 'Antrenman planı görüntüleme sonrası', 'Çok Yüksek'],
                    ['Pinterest', 'İlham / Keşif', 'Moodboard oluşturan, estetik arayan, hediye fikri keşfeden kitle', 'Parfüm/hediye pini keşfetme anı', 'Yüksek'],
                    ['AllTrails / Komoot', 'Outdoor', 'Trail/hiking rotası planlayan doğa sever', 'Rota kaydedildiğinde — ekipman ihtiyacı hatırlatma', 'Yüksek'],
                    ['MyFitnessPal / Yazio', 'Beslenme', 'Kalori/makro takip eden sağlık bilincli kullanıcı', 'Günlük hedef tamamlama sonrası', 'Orta'],
                    ['Calm / Headspace', 'Wellness', 'Mindfulness, stres yönetimi — wellness yaşam tarzı', 'Meditasyon sonrası — "vücut için de yatırım yap"', 'Orta'],
                    ['Spotify (Spor Playlistleri)', 'Müzik', 'Koşu/gym playlist dinleyicisi, antrenman anında', 'Workout playlist arası reklam', 'Yüksek'],
                    ['TripAdvisor / Google Maps', 'Seyahat', 'İstanbul\'da "parfüm butik" arayan turist veya lokal', 'Butik/mağaza keşfetme anı — "Made in Istanbul"', 'Orta'],
                  ].map(([name, cat, profile, moment, priority], i) => (
                    <tr key={i}>
                      <td className="py-2.5 px-4 font-medium text-gray-900">{name}</td>
                      <td className="py-2.5 px-4 text-gray-500">{cat}</td>
                      <td className="py-2.5 px-4 text-gray-600 text-xs">{profile}</td>
                      <td className="py-2.5 px-4 text-gray-600 text-xs">{moment}</td>
                      <td className="py-2.5 px-4 text-center">
                        <span className={`inline-flex px-2 py-0.5 text-[10px] font-bold rounded ${
                          priority === 'Çok Yüksek' ? 'bg-gray-900 text-white' :
                          priority === 'Yüksek' ? 'bg-gray-700 text-white' :
                          'bg-gray-200 text-gray-700'
                        }`}>{priority}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      </div>

      {/* ───────── Medya Planı — Branding ───────── */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-bold text-gray-900">Medya Planı</h2>
          <p className="text-xs text-gray-400 mt-1">Branding ve Performance ana başlıklarında platform bazlı yüzdesel yatırım dağılımı</p>
        </div>
        <div className="p-6">

          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="rounded-lg border p-4">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Branding</p>
              <p className="text-2xl font-black text-gray-900 mt-1">%35</p>
              <p className="text-[10px] text-gray-400 mt-0.5">Awareness · Reach · Video</p>
            </div>
            <div className="rounded-lg border p-4">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Performance</p>
              <p className="text-2xl font-black text-gray-900 mt-1">%65</p>
              <p className="text-[10px] text-gray-400 mt-0.5">Conversion · ROAS · Retargeting</p>
            </div>
            <div className="rounded-lg border p-4">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Platform Sayısı</p>
              <p className="text-2xl font-black text-gray-900 mt-1">8</p>
              <p className="text-[10px] text-gray-400 mt-0.5">Google, Meta, TikTok, YouTube…</p>
            </div>
            <div className="rounded-lg border p-4">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Kampanya Tipi</p>
              <p className="text-2xl font-black text-gray-900 mt-1">16</p>
              <p className="text-[10px] text-gray-400 mt-0.5">Search, PMAX, DPA, Spark Ads…</p>
            </div>
          </div>

          {/* ── SINGLE COMBINED TABLE ── */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr>
                  <th className="text-left py-3 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider border-b-2 border-gray-900" rowSpan="2">Platform</th>
                  <th className="text-center py-2 px-4 text-[10px] font-bold text-blue-600 uppercase tracking-wider border-b border-blue-200 border-r-2 border-r-gray-300" colSpan="2">Branding</th>
                  <th className="text-center py-2 px-4 text-[10px] font-bold text-emerald-600 uppercase tracking-wider border-b border-emerald-200" colSpan="2">Performance</th>
                  <th className="text-center py-3 px-4 text-[10px] font-bold text-gray-900 uppercase tracking-wider border-b-2 border-gray-900 border-l-2 border-l-gray-300 bg-gray-50" rowSpan="2">Toplam</th>
                </tr>
                <tr>
                  <th className="text-left py-2 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider border-b-2 border-gray-900">Campaign Types</th>
                  <th className="text-center py-2 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider border-b-2 border-gray-900 border-r-2 border-r-gray-300 w-[7%]">Pay</th>
                  <th className="text-left py-2 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider border-b-2 border-gray-900">Campaign Types</th>
                  <th className="text-center py-2 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider border-b-2 border-gray-900 w-[7%]">Pay</th>
                </tr>
              </thead>
              <tbody className="text-gray-700">

                {/* Google Ads */}
                <tr className="border-b border-gray-200">
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-2.5">
                      <img src="/google_ads-512.webp" alt="Google Ads" className="w-7 h-7 object-contain" />
                      <span className="font-bold text-gray-900">Google Ads</span>
                    </div>
                  </td>
                  <td className="py-4 px-4 text-gray-300 text-xs">—</td>
                  <td className="py-4 px-4 text-center text-gray-300 border-r-2 border-r-gray-300">—</td>
                  <td className="py-4 px-4 text-xs text-gray-600">Search (Brand+Generic, Conquest) · Performance Max (PMAX) · Shopping · RLSA</td>
                  <td className="py-4 px-4 text-center"><span className="font-bold text-gray-900">%25</span></td>
                  <td className="py-4 px-4 text-center border-l-2 border-l-gray-300 bg-gray-50"><span className="text-base font-black text-gray-900">%25</span></td>
                </tr>

                {/* Meta Ads */}
                <tr className="border-b border-gray-200 bg-gray-50/30">
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-2.5">
                      <img src="/6033716.png" alt="Meta" className="w-7 h-7 object-contain" />
                      <span className="font-bold text-gray-900">Meta Ads</span>
                    </div>
                  </td>
                  <td className="py-4 px-4 text-xs text-gray-600">Reach &amp; Frequency · Video Views (Reels, Stories) · Brand Awareness</td>
                  <td className="py-4 px-4 text-center border-r-2 border-r-gray-300"><span className="font-bold text-gray-900">%8</span></td>
                  <td className="py-4 px-4 text-xs text-gray-600">Sales (Advantage+ Shopping) · DPA · Conversion Optimize · Catalog Retargeting</td>
                  <td className="py-4 px-4 text-center"><span className="font-bold text-gray-900">%18</span></td>
                  <td className="py-4 px-4 text-center border-l-2 border-l-gray-300 bg-gray-50"><span className="text-base font-black text-gray-900">%26</span></td>
                </tr>

                {/* YouTube */}
                <tr className="border-b border-gray-200">
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-2.5">
                      <img src="/youtube.svg" alt="YouTube" className="w-7 h-7 object-contain" />
                      <span className="font-bold text-gray-900">YouTube</span>
                    </div>
                  </td>
                  <td className="py-4 px-4 text-xs text-gray-600">In-Stream (Skippable + Non-skip) · Bumper 6s · Video Discovery</td>
                  <td className="py-4 px-4 text-center border-r-2 border-r-gray-300"><span className="font-bold text-gray-900">%10</span></td>
                  <td className="py-4 px-4 text-gray-300 text-xs">—</td>
                  <td className="py-4 px-4 text-center text-gray-300">—</td>
                  <td className="py-4 px-4 text-center border-l-2 border-l-gray-300 bg-gray-50"><span className="text-base font-black text-gray-900">%10</span></td>
                </tr>

                {/* TikTok */}
                <tr className="border-b border-gray-200 bg-gray-50/30">
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-2.5">
                      <img src="/tiktokv4.svg" alt="TikTok" className="w-7 h-7 object-contain" />
                      <span className="font-bold text-gray-900">TikTok</span>
                    </div>
                  </td>
                  <td className="py-4 px-4 text-xs text-gray-600">TopView / Top Feed · Spark Ads (UGC Boost) · Reach &amp; Video Views</td>
                  <td className="py-4 px-4 text-center border-r-2 border-r-gray-300"><span className="font-bold text-gray-900">%5</span></td>
                  <td className="py-4 px-4 text-xs text-gray-600">Conversion (Website) · Catalog Ads · Video Shopping Ads</td>
                  <td className="py-4 px-4 text-center"><span className="font-bold text-gray-900">%5</span></td>
                  <td className="py-4 px-4 text-center border-l-2 border-l-gray-300 bg-gray-50"><span className="text-base font-black text-gray-900">%10</span></td>
                </tr>

                {/* RTB House */}
                <tr className="border-b border-gray-200">
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-2.5">
                      <img src="/29374191.png" alt="RTB House" className="w-7 h-7 object-contain" />
                      <span className="font-bold text-gray-900">RTB House</span>
                    </div>
                  </td>
                  <td className="py-4 px-4 text-gray-300 text-xs">—</td>
                  <td className="py-4 px-4 text-center text-gray-300 border-r-2 border-r-gray-300">—</td>
                  <td className="py-4 px-4 text-xs text-gray-600">Deep Learning Retargeting · Dynamic Product Banner · Cross-Device</td>
                  <td className="py-4 px-4 text-center"><span className="font-bold text-gray-900">%7</span></td>
                  <td className="py-4 px-4 text-center border-l-2 border-l-gray-300 bg-gray-50"><span className="text-base font-black text-gray-900">%7</span></td>
                </tr>

                {/* Programmatic */}
                <tr className="border-b border-gray-200 bg-gray-50/30">
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-2.5">
                      <img src="/programmatic-logo.svg" alt="Programmatic" className="w-7 h-7 object-contain" />
                      <span className="font-bold text-gray-900">Programmatic</span>
                    </div>
                  </td>
                  <td className="py-4 px-4 text-xs text-gray-600">Rich Media Display (DV360) · Native (Taboola, Outbrain) · OLV Pre-roll</td>
                  <td className="py-4 px-4 text-center border-r-2 border-r-gray-300"><span className="font-bold text-gray-900">%5</span></td>
                  <td className="py-4 px-4 text-xs text-gray-600">DCO (Dynamic Creative) · Site Retargeting (30/60/90 gün)</td>
                  <td className="py-4 px-4 text-center"><span className="font-bold text-gray-900">%3</span></td>
                  <td className="py-4 px-4 text-center border-l-2 border-l-gray-300 bg-gray-50"><span className="text-base font-black text-gray-900">%8</span></td>
                </tr>

                {/* Pinterest */}
                <tr className="border-b border-gray-200">
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-2.5">
                      <img src="/pinterest-logo.svg" alt="Pinterest" className="w-7 h-7 object-contain" />
                      <span className="font-bold text-gray-900">Pinterest</span>
                    </div>
                  </td>
                  <td className="py-4 px-4 text-xs text-gray-600">Idea Pins (Lifestyle) · Awareness Kampanyaları</td>
                  <td className="py-4 px-4 text-center border-r-2 border-r-gray-300"><span className="font-bold text-gray-900">%3</span></td>
                  <td className="py-4 px-4 text-xs text-gray-600">Shopping Ads (Catalog) · Collection Ads</td>
                  <td className="py-4 px-4 text-center"><span className="font-bold text-gray-900">%3</span></td>
                  <td className="py-4 px-4 text-center border-l-2 border-l-gray-300 bg-gray-50"><span className="text-base font-black text-gray-900">%6</span></td>
                </tr>

                {/* Yandex */}
                <tr className="border-b border-gray-200 bg-gray-50/30">
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-2.5">
                      <img src="/Yandex_Start_logo_(en).png" alt="Yandex" className="w-7 h-7 object-contain" />
                      <span className="font-bold text-gray-900">Yandex</span>
                    </div>
                  </td>
                  <td className="py-4 px-4 text-xs text-gray-600">Navigasyon Pin Reklam · Rota Üzeri Banner</td>
                  <td className="py-4 px-4 text-center border-r-2 border-r-gray-300"><span className="font-bold text-gray-900">%4</span></td>
                  <td className="py-4 px-4 text-xs text-gray-600">Smart Banner (Dynamic) · Display Remarketing</td>
                  <td className="py-4 px-4 text-center"><span className="font-bold text-gray-900">%4</span></td>
                  <td className="py-4 px-4 text-center border-l-2 border-l-gray-300 bg-gray-50"><span className="text-base font-black text-gray-900">%8</span></td>
                </tr>

              </tbody>
              <tfoot>
                <tr className="border-t-2 border-gray-900 bg-gray-100">
                  <td className="py-3 px-4 font-bold text-gray-900">TOPLAM</td>
                  <td className="py-3 px-4"></td>
                  <td className="py-3 px-4 text-center border-r-2 border-r-gray-300"><span className="text-base font-black text-blue-700">%35</span></td>
                  <td className="py-3 px-4"></td>
                  <td className="py-3 px-4 text-center"><span className="text-base font-black text-emerald-700">%65</span></td>
                  <td className="py-3 px-4 text-center border-l-2 border-l-gray-300 bg-gray-200"><span className="text-lg font-black text-gray-900">%100</span></td>
                </tr>
                <tr className="bg-gray-900 text-white">
                  <td className="py-3 px-4 font-bold rounded-bl-lg">Grand Total</td>
                  <td className="py-3 px-4" colSpan="4">
                    <span className="text-xs text-neutral-400">8 Platform · 16 Campaign Type · Branding %35 + Performance %65</span>
                  </td>
                  <td className="py-3 px-4 text-center font-black text-xl rounded-br-lg">%100</td>
                </tr>
              </tfoot>
            </table>
          </div>

        </div>
      </div>

      {/* ───────── Müşteri Segmentasyonu ───────── */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-bold text-gray-900">Müşteri Segmentasyonu</h2>
          <p className="text-xs text-gray-400 mt-1">Mevcut müşteri tabanının davranışsal ve değer bazlı segmentlere ayrılması — remarketing, CRM ve otomasyon senaryoları</p>
        </div>
        <div className="p-6">
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b-2 border-gray-900">
                  <th className="text-left py-3 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider w-[5%]">#</th>
                  <th className="text-left py-3 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider w-[18%]">Segment Adı</th>
                  <th className="text-left py-3 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider w-[24%]">Tanım / Kural</th>
                  <th className="text-left py-3 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider w-[20%]">Aksiyon</th>
                  <th className="text-left py-3 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider w-[18%]">Kanal</th>
                  <th className="text-center py-3 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider w-[8%]">Öncelik</th>
                  <th className="text-center py-3 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider w-[7%]">Tahmini Oran</th>
                </tr>
              </thead>
              <tbody className="text-gray-700">

                {/* 1 — Uyuyan Müşteriler */}
                <tr className="border-b border-gray-200">
                  <td className="py-4 px-4 text-gray-400 font-mono text-xs">01</td>
                  <td className="py-4 px-4">
                    <span className="font-bold text-gray-900">Uyuyan Müşteriler</span>
                    <span className="ml-2 inline-block px-2 py-0.5 bg-red-50 text-red-600 text-[10px] font-bold rounded-full">Win-back</span>
                  </td>
                  <td className="py-4 px-4 text-xs text-gray-600">Son 90+ gündür sipariş vermeyen, daha önce en az 1 satın alma yapmış kullanıcılar</td>
                  <td className="py-4 px-4 text-xs text-gray-600">Kişiselleştirilmiş indirim kodu + &quot;Seni özledik&quot; kampanyası · Önceki satın alma kategorisine göre ürün önerisi</td>
                  <td className="py-4 px-4 text-xs text-gray-600">E-posta · Push · Meta DPA · RTB House Retargeting</td>
                  <td className="py-4 px-4 text-center"><span className="inline-block px-2.5 py-1 bg-red-100 text-red-700 text-[10px] font-bold rounded-full">Yüksek</span></td>
                  <td className="py-4 px-4 text-center text-xs font-bold text-gray-900">~%25</td>
                </tr>

                {/* 2 — Tekrar Eden Alıcılar */}
                <tr className="border-b border-gray-200 bg-gray-50/30">
                  <td className="py-4 px-4 text-gray-400 font-mono text-xs">02</td>
                  <td className="py-4 px-4">
                    <span className="font-bold text-gray-900">Tekrar Eden Alıcılar</span>
                    <span className="ml-2 inline-block px-2 py-0.5 bg-emerald-50 text-emerald-600 text-[10px] font-bold rounded-full">Loyal</span>
                  </td>
                  <td className="py-4 px-4 text-xs text-gray-600">2+ sipariş veren kullanıcılar — marka sadakati potansiyeli yüksek grup</td>
                  <td className="py-4 px-4 text-xs text-gray-600">Early access kampanyaları · VIP indirim · Cross-sell (parfüm aldıysa discovery set/ev kokusu öner)</td>
                  <td className="py-4 px-4 text-xs text-gray-600">E-posta · SMS · WhatsApp · Meta Lookalike Seed</td>
                  <td className="py-4 px-4 text-center"><span className="inline-block px-2.5 py-1 bg-emerald-100 text-emerald-700 text-[10px] font-bold rounded-full">Yüksek</span></td>
                  <td className="py-4 px-4 text-center text-xs font-bold text-gray-900">~%12</td>
                </tr>

                {/* 3 — Kategori Spesifik */}
                <tr className="border-b border-gray-200">
                  <td className="py-4 px-4 text-gray-400 font-mono text-xs">03</td>
                  <td className="py-4 px-4">
                    <span className="font-bold text-gray-900">Go Run Alıcıları</span>
                    <span className="ml-2 inline-block px-2 py-0.5 bg-blue-50 text-blue-600 text-[10px] font-bold rounded-full">Kategori</span>
                  </td>
                  <td className="py-4 px-4 text-xs text-gray-600">skechers.com.tr üzerinden Go Run serisi satın almış kullanıcılar</td>
                  <td className="py-4 px-4 text-xs text-gray-600">Yeni sezon Go Run modelleri tanıtım · Koşu aksesuarları cross-sell · Sezon değişiminde yenileme hatırlatması</td>
                  <td className="py-4 px-4 text-xs text-gray-600">E-posta · Google RLSA · Meta Custom Audience</td>
                  <td className="py-4 px-4 text-center"><span className="inline-block px-2.5 py-1 bg-blue-100 text-blue-700 text-[10px] font-bold rounded-full">Orta</span></td>
                  <td className="py-4 px-4 text-center text-xs font-bold text-gray-900">~%8</td>
                </tr>

                {/* 4 — Yüksek AOV */}
                <tr className="border-b border-gray-200 bg-gray-50/30">
                  <td className="py-4 px-4 text-gray-400 font-mono text-xs">04</td>
                  <td className="py-4 px-4">
                    <span className="font-bold text-gray-900">Yüksek Sepet Ortalaması</span>
                    <span className="ml-2 inline-block px-2 py-0.5 bg-amber-50 text-amber-600 text-[10px] font-bold rounded-full">High AOV</span>
                  </td>
                  <td className="py-4 px-4 text-xs text-gray-600">Ortalama sepet tutarı site genelinin 1.5x üzerinde olan kullanıcılar (premium alıcı profili)</td>
                  <td className="py-4 px-4 text-xs text-gray-600">Premium koleksiyon lansmanları · Ücretsiz kargo · Bundle teklifleri · Kişiye özel kupon</td>
                  <td className="py-4 px-4 text-xs text-gray-600">E-posta · SMS · RTB House · Programmatic DCO</td>
                  <td className="py-4 px-4 text-center"><span className="inline-block px-2.5 py-1 bg-red-100 text-red-700 text-[10px] font-bold rounded-full">Yüksek</span></td>
                  <td className="py-4 px-4 text-center text-xs font-bold text-gray-900">~%10</td>
                </tr>

                {/* 5 — Tek Seferlik */}
                <tr className="border-b border-gray-200">
                  <td className="py-4 px-4 text-gray-400 font-mono text-xs">05</td>
                  <td className="py-4 px-4">
                    <span className="font-bold text-gray-900">Tek Seferlik Alıcılar</span>
                    <span className="ml-2 inline-block px-2 py-0.5 bg-orange-50 text-orange-600 text-[10px] font-bold rounded-full">One-timer</span>
                  </td>
                  <td className="py-4 px-4 text-xs text-gray-600">Yalnızca 1 kez sipariş vermiş ve son 30-90 gün içinde tekrar gelmemiş kullanıcılar</td>
                  <td className="py-4 px-4 text-xs text-gray-600">İkinci alışverişe özel %15 indirim · &quot;İlk siparişini beğendin mi?&quot; anket + ürün önerisi</td>
                  <td className="py-4 px-4 text-xs text-gray-600">E-posta · Push · Meta Retargeting · Google Display</td>
                  <td className="py-4 px-4 text-center"><span className="inline-block px-2.5 py-1 bg-orange-100 text-orange-700 text-[10px] font-bold rounded-full">Orta</span></td>
                  <td className="py-4 px-4 text-center text-xs font-bold text-gray-900">~%35</td>
                </tr>

                {/* 6 — Sepet Terk */}
                <tr className="border-b border-gray-200 bg-gray-50/30">
                  <td className="py-4 px-4 text-gray-400 font-mono text-xs">06</td>
                  <td className="py-4 px-4">
                    <span className="font-bold text-gray-900">Sepet Terk Edenler</span>
                    <span className="ml-2 inline-block px-2 py-0.5 bg-purple-50 text-purple-600 text-[10px] font-bold rounded-full">Cart Abandon</span>
                  </td>
                  <td className="py-4 px-4 text-xs text-gray-600">Sepete ürün ekleyip satın almadan çıkan kullanıcılar (son 7 gün aktif)</td>
                  <td className="py-4 px-4 text-xs text-gray-600">Sepetteki ürünle dinamik reklam · 24 saat sonra hatırlatma e-posta · 48 saat sonra sınırlı indirim</td>
                  <td className="py-4 px-4 text-xs text-gray-600">E-posta · Meta DPA · RTB House · Google RLSA</td>
                  <td className="py-4 px-4 text-center"><span className="inline-block px-2.5 py-1 bg-red-100 text-red-700 text-[10px] font-bold rounded-full">Yüksek</span></td>
                  <td className="py-4 px-4 text-center text-xs font-bold text-gray-900">~%18</td>
                </tr>

                {/* 7 — İndirim Avcısı */}
                <tr className="border-b border-gray-200">
                  <td className="py-4 px-4 text-gray-400 font-mono text-xs">07</td>
                  <td className="py-4 px-4">
                    <span className="font-bold text-gray-900">İndirim Avcıları</span>
                    <span className="ml-2 inline-block px-2 py-0.5 bg-pink-50 text-pink-600 text-[10px] font-bold rounded-full">Promo Hunter</span>
                  </td>
                  <td className="py-4 px-4 text-xs text-gray-600">Yalnızca kampanya/indirim dönemlerinde alışveriş yapan, kupon kullanan kullanıcılar</td>
                  <td className="py-4 px-4 text-xs text-gray-600">Flash sale öncesi erken bildirim · Sınırlı stok urgency mesajları · Full price ürünlere kademeli indirim denemeleri</td>
                  <td className="py-4 px-4 text-xs text-gray-600">E-posta · Push · SMS</td>
                  <td className="py-4 px-4 text-center"><span className="inline-block px-2.5 py-1 bg-orange-100 text-orange-700 text-[10px] font-bold rounded-full">Orta</span></td>
                  <td className="py-4 px-4 text-center text-xs font-bold text-gray-900">~%15</td>
                </tr>

                {/* 8 — Sezonsal Alıcı */}
                <tr className="border-b border-gray-200 bg-gray-50/30">
                  <td className="py-4 px-4 text-gray-400 font-mono text-xs">08</td>
                  <td className="py-4 px-4">
                    <span className="font-bold text-gray-900">Sezonsal Alıcılar</span>
                    <span className="ml-2 inline-block px-2 py-0.5 bg-cyan-50 text-cyan-600 text-[10px] font-bold rounded-full">Seasonal</span>
                  </td>
                  <td className="py-4 px-4 text-xs text-gray-600">Belirli dönemlerde (okul açılışı, yaz sezonu, Black Friday) alışveriş yapan döngüsel alıcılar</td>
                  <td className="py-4 px-4 text-xs text-gray-600">Sezon başlamadan 2 hafta önce hatırlatma · Geçen yıl aldığı kategoriden yeni model önerisi</td>
                  <td className="py-4 px-4 text-xs text-gray-600">E-posta · Meta · Google Ads Remarketing</td>
                  <td className="py-4 px-4 text-center"><span className="inline-block px-2.5 py-1 bg-blue-100 text-blue-700 text-[10px] font-bold rounded-full">Orta</span></td>
                  <td className="py-4 px-4 text-center text-xs font-bold text-gray-900">~%20</td>
                </tr>

                {/* 9 — Çoklu Kategori */}
                <tr className="border-b border-gray-200">
                  <td className="py-4 px-4 text-gray-400 font-mono text-xs">09</td>
                  <td className="py-4 px-4">
                    <span className="font-bold text-gray-900">Çoklu Kategori Alıcıları</span>
                    <span className="ml-2 inline-block px-2 py-0.5 bg-violet-50 text-violet-600 text-[10px] font-bold rounded-full">Multi-cat</span>
                  </td>
                  <td className="py-4 px-4 text-xs text-gray-600">2+ farklı koleksiyondan (Exclusive + Heritage, parfüm + discovery set) ürün satın almış kullanıcılar</td>
                  <td className="py-4 px-4 text-xs text-gray-600">Henüz keşfetmediği 3. kategori önerisi · Kombin/set teklifleri · Lifestyle içerik kampanyaları</td>
                  <td className="py-4 px-4 text-xs text-gray-600">E-posta · Meta Lookalike · Programmatic</td>
                  <td className="py-4 px-4 text-center"><span className="inline-block px-2.5 py-1 bg-emerald-100 text-emerald-700 text-[10px] font-bold rounded-full">Yüksek</span></td>
                  <td className="py-4 px-4 text-center text-xs font-bold text-gray-900">~%6</td>
                </tr>

              </tbody>
            </table>
          </div>

          {/* Segment Özet Kartları */}
          <div className="mt-6 grid grid-cols-3 gap-4">
            <div className="rounded-xl bg-red-50 border border-red-100 p-4">
              <p className="text-[10px] font-bold text-red-400 uppercase tracking-wider">Yüksek Öncelik</p>
              <p className="text-2xl font-black text-red-700 mt-1">4 Segment</p>
              <p className="text-xs text-red-500 mt-1">Uyuyan · Tekrar Eden · Yüksek AOV · Sepet Terk · Çoklu Kategori</p>
            </div>
            <div className="rounded-xl bg-amber-50 border border-amber-100 p-4">
              <p className="text-[10px] font-bold text-amber-400 uppercase tracking-wider">Orta Öncelik</p>
              <p className="text-2xl font-black text-amber-700 mt-1">4 Segment</p>
              <p className="text-xs text-amber-500 mt-1">Go Run · Tek Seferlik · İndirim Avcısı · Sezonsal</p>
            </div>
            <div className="rounded-xl bg-gray-900 p-4">
              <p className="text-[10px] font-bold text-neutral-400 uppercase tracking-wider">Toplam Segment</p>
              <p className="text-2xl font-black text-white mt-1">9 Segment</p>
              <p className="text-xs text-neutral-400 mt-1">RFM + Kategori + Davranışsal kırılım</p>
            </div>
          </div>
        </div>
      </div>

      {/* ───────── 12 Aylık Bütçe Dağılımı ───────── */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-bold text-gray-900">12 Aylık Bütçe Dağılımı</h2>
          <p className="text-xs text-gray-400 mt-1">Aylık yüzdesel yatırım dağılımı — sezonalite, kampanya dönemleri ve tüketici davranış döngüsüne göre ağırlıklandırılmıştır</p>
        </div>
        <div className="p-6">

          {/* Bar Chart Visual */}
          <div className="flex items-end gap-2 h-32 mb-2 px-2">
            {[
              { ay: 'Oca', pct: 9, color: 'bg-blue-500' },
              { ay: 'Şub', pct: 8, color: 'bg-blue-500' },
              { ay: 'Mar', pct: 9, color: 'bg-blue-500' },
              { ay: 'Nis', pct: 7, color: 'bg-gray-400' },
              { ay: 'May', pct: 7, color: 'bg-gray-400' },
              { ay: 'Haz', pct: 6, color: 'bg-gray-300' },
              { ay: 'Tem', pct: 5, color: 'bg-gray-300' },
              { ay: 'Ağu', pct: 6, color: 'bg-gray-300' },
              { ay: 'Eyl', pct: 10, color: 'bg-emerald-500' },
              { ay: 'Eki', pct: 9, color: 'bg-emerald-500' },
              { ay: 'Kas', pct: 14, color: 'bg-orange-500' },
              { ay: 'Ara', pct: 10, color: 'bg-red-500' },
            ].map((m, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-1">
                <span className="text-[11px] font-black text-gray-900">%{m.pct}</span>
                <div className={`w-full rounded-t-md ${m.color}`} style={{ height: `${(m.pct / 14) * 100}%` }}></div>
                <span className="text-[10px] font-bold text-gray-500 mt-1">{m.ay}</span>
              </div>
            ))}
          </div>

          {/* Detail Table */}
          <div className="overflow-x-auto mt-6">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b-2 border-gray-900">
                  <th className="text-left py-3 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Ay</th>
                  <th className="text-center py-3 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Pay (%)</th>
                  <th className="text-left py-3 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Dönem</th>
                  <th className="text-left py-3 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Sezon Notları / Önemli Günler</th>
                  <th className="text-center py-3 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Yoğunluk</th>
                </tr>
              </thead>
              <tbody className="text-gray-700">

                <tr className="border-b border-gray-200">
                  <td className="py-3 px-4 font-bold text-gray-900">Ocak</td>
                  <td className="py-3 px-4 text-center"><span className="text-base font-black text-gray-900">%9</span></td>
                  <td className="py-3 px-4"><span className="inline-block px-2 py-0.5 bg-blue-50 text-blue-600 text-[10px] font-bold rounded-full">Q1</span></td>
                  <td className="py-3 px-4 text-xs text-gray-600">Yeni yıl hediye kartı kullanımı · Kış parfümleri push (oud, amber) · Discovery set kampanyası · Ocak yenilenme dönemi</td>
                  <td className="py-3 px-4 text-center">
                    <div className="flex items-center justify-center gap-0.5">
                      <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                      <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                      <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                      <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                      <div className="w-2 h-2 rounded-full bg-gray-200"></div>
                    </div>
                  </td>
                </tr>

                <tr className="border-b border-gray-200 bg-gray-50/30">
                  <td className="py-3 px-4 font-bold text-gray-900">Şubat</td>
                  <td className="py-3 px-4 text-center"><span className="text-base font-black text-gray-900">%8</span></td>
                  <td className="py-3 px-4"><span className="inline-block px-2 py-0.5 bg-blue-50 text-blue-600 text-[10px] font-bold rounded-full">Q1</span></td>
                  <td className="py-3 px-4 text-xs text-gray-600">Sevgililer Günü (hediye peak) · Kış koleksiyonu son push · İlkbahar taze kokular teaser · Yarıyıl tatili</td>
                  <td className="py-3 px-4 text-center">
                    <div className="flex items-center justify-center gap-0.5">
                      <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                      <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                      <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                      <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                      <div className="w-2 h-2 rounded-full bg-gray-200"></div>
                    </div>
                  </td>
                </tr>

                <tr className="border-b border-gray-200">
                  <td className="py-3 px-4 font-bold text-gray-900">Mart</td>
                  <td className="py-3 px-4 text-center"><span className="text-base font-black text-gray-900">%9</span></td>
                  <td className="py-3 px-4"><span className="inline-block px-2 py-0.5 bg-blue-50 text-blue-600 text-[10px] font-bold rounded-full">Q1</span></td>
                  <td className="py-3 px-4 text-xs text-gray-600">8 Mart Kadınlar Günü (hediye) · İlkbahar/yaz parfüm lansmanı · Taze & çiçeksi notalar öne çıkar · Influencer kampanyası başlangıcı</td>
                  <td className="py-3 px-4 text-center">
                    <div className="flex items-center justify-center gap-0.5">
                      <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                      <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                      <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                      <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                      <div className="w-2 h-2 rounded-full bg-gray-200"></div>
                    </div>
                  </td>
                </tr>

                <tr className="border-b border-gray-200 bg-gray-50/30">
                  <td className="py-3 px-4 font-bold text-gray-900">Nisan</td>
                  <td className="py-3 px-4 text-center"><span className="text-base font-black text-gray-900">%7</span></td>
                  <td className="py-3 px-4"><span className="inline-block px-2 py-0.5 bg-violet-50 text-violet-600 text-[10px] font-bold rounded-full">Q2</span></td>
                  <td className="py-3 px-4 text-xs text-gray-600">Ramazan Bayramı hediye dönemi · Bahar parfüm geçişi · Turizm sezonu başlangıcı · İstanbul butik push</td>
                  <td className="py-3 px-4 text-center">
                    <div className="flex items-center justify-center gap-0.5">
                      <div className="w-2 h-2 rounded-full bg-gray-400"></div>
                      <div className="w-2 h-2 rounded-full bg-gray-400"></div>
                      <div className="w-2 h-2 rounded-full bg-gray-400"></div>
                      <div className="w-2 h-2 rounded-full bg-gray-200"></div>
                      <div className="w-2 h-2 rounded-full bg-gray-200"></div>
                    </div>
                  </td>
                </tr>

                <tr className="border-b border-gray-200">
                  <td className="py-3 px-4 font-bold text-gray-900">Mayıs</td>
                  <td className="py-3 px-4 text-center"><span className="text-base font-black text-gray-900">%7</span></td>
                  <td className="py-3 px-4"><span className="inline-block px-2 py-0.5 bg-violet-50 text-violet-600 text-[10px] font-bold rounded-full">Q2</span></td>
                  <td className="py-3 px-4 text-xs text-gray-600">Anneler Günü (hediye peak) · Kurban Bayramı hediye · Yaz parfümleri öne çıkar · Export pazarı push</td>
                  <td className="py-3 px-4 text-center">
                    <div className="flex items-center justify-center gap-0.5">
                      <div className="w-2 h-2 rounded-full bg-gray-400"></div>
                      <div className="w-2 h-2 rounded-full bg-gray-400"></div>
                      <div className="w-2 h-2 rounded-full bg-gray-400"></div>
                      <div className="w-2 h-2 rounded-full bg-gray-200"></div>
                      <div className="w-2 h-2 rounded-full bg-gray-200"></div>
                    </div>
                  </td>
                </tr>

                <tr className="border-b border-gray-200 bg-gray-50/30">
                  <td className="py-3 px-4 font-bold text-gray-900">Haziran</td>
                  <td className="py-3 px-4 text-center"><span className="text-base font-black text-gray-900">%6</span></td>
                  <td className="py-3 px-4"><span className="inline-block px-2 py-0.5 bg-violet-50 text-violet-600 text-[10px] font-bold rounded-full">Q2</span></td>
                  <td className="py-3 px-4 text-xs text-gray-600">Babalar Günü (hediye) · Yaz tatili — turist trafiği peak · Bodrum/Çeşme pop-up fırsatı · Hafif kokular öne çıkar</td>
                  <td className="py-3 px-4 text-center">
                    <div className="flex items-center justify-center gap-0.5">
                      <div className="w-2 h-2 rounded-full bg-gray-300"></div>
                      <div className="w-2 h-2 rounded-full bg-gray-300"></div>
                      <div className="w-2 h-2 rounded-full bg-gray-200"></div>
                      <div className="w-2 h-2 rounded-full bg-gray-200"></div>
                      <div className="w-2 h-2 rounded-full bg-gray-200"></div>
                    </div>
                  </td>
                </tr>

                <tr className="border-b border-gray-200">
                  <td className="py-3 px-4 font-bold text-gray-900">Temmuz</td>
                  <td className="py-3 px-4 text-center"><span className="text-base font-black text-gray-900">%5</span></td>
                  <td className="py-3 px-4"><span className="inline-block px-2 py-0.5 bg-amber-50 text-amber-600 text-[10px] font-bold rounded-full">Q3</span></td>
                  <td className="py-3 px-4 text-xs text-gray-600">Yaz tatili peak — turist & expat hedefleme · Online satış düşük · Retargeting & CRM focus · İçerik üretim dönemi</td>
                  <td className="py-3 px-4 text-center">
                    <div className="flex items-center justify-center gap-0.5">
                      <div className="w-2 h-2 rounded-full bg-gray-300"></div>
                      <div className="w-2 h-2 rounded-full bg-gray-300"></div>
                      <div className="w-2 h-2 rounded-full bg-gray-200"></div>
                      <div className="w-2 h-2 rounded-full bg-gray-200"></div>
                      <div className="w-2 h-2 rounded-full bg-gray-200"></div>
                    </div>
                  </td>
                </tr>

                <tr className="border-b border-gray-200 bg-gray-50/30">
                  <td className="py-3 px-4 font-bold text-gray-900">Ağustos</td>
                  <td className="py-3 px-4 text-center"><span className="text-base font-black text-gray-900">%6</span></td>
                  <td className="py-3 px-4"><span className="inline-block px-2 py-0.5 bg-amber-50 text-amber-600 text-[10px] font-bold rounded-full">Q3</span></td>
                  <td className="py-3 px-4 text-xs text-gray-600">Yaz sonu — sonbahar koleksiyonu teaser · Tatilden dönüş parfüm yenilenme · A/B test sonuçları · SEO içerik push</td>
                  <td className="py-3 px-4 text-center">
                    <div className="flex items-center justify-center gap-0.5">
                      <div className="w-2 h-2 rounded-full bg-gray-300"></div>
                      <div className="w-2 h-2 rounded-full bg-gray-300"></div>
                      <div className="w-2 h-2 rounded-full bg-gray-200"></div>
                      <div className="w-2 h-2 rounded-full bg-gray-200"></div>
                      <div className="w-2 h-2 rounded-full bg-gray-200"></div>
                    </div>
                  </td>
                </tr>

                <tr className="border-b border-gray-200">
                  <td className="py-3 px-4 font-bold text-gray-900">Eylül</td>
                  <td className="py-3 px-4 text-center"><span className="text-base font-black text-emerald-700">%10</span></td>
                  <td className="py-3 px-4"><span className="inline-block px-2 py-0.5 bg-amber-50 text-amber-600 text-[10px] font-bold rounded-full">Q3</span></td>
                  <td className="py-3 px-4 text-xs text-gray-600">Sonbahar koleksiyonu lansmanı · Ağır/sıcak notalar öne çıkar · Koku festivali dönemi · Online satış toparlanma</td>
                  <td className="py-3 px-4 text-center">
                    <div className="flex items-center justify-center gap-0.5">
                      <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                      <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                      <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                      <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                      <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                    </div>
                  </td>
                </tr>

                <tr className="border-b border-gray-200 bg-gray-50/30">
                  <td className="py-3 px-4 font-bold text-gray-900">Ekim</td>
                  <td className="py-3 px-4 text-center"><span className="text-base font-black text-gray-900">%9</span></td>
                  <td className="py-3 px-4"><span className="inline-block px-2 py-0.5 bg-emerald-50 text-emerald-600 text-[10px] font-bold rounded-full">Q4</span></td>
                  <td className="py-3 px-4 text-xs text-gray-600">Sonbahar yoğun kokular push · Black Friday pre-heat · 11.11 Singles Day · Hediye sezonu ısınma</td>
                  <td className="py-3 px-4 text-center">
                    <div className="flex items-center justify-center gap-0.5">
                      <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                      <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                      <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                      <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                      <div className="w-2 h-2 rounded-full bg-gray-200"></div>
                    </div>
                  </td>
                </tr>

                <tr className="border-b border-gray-200 bg-orange-50/50">
                  <td className="py-3 px-4 font-bold text-orange-700">Kasım</td>
                  <td className="py-3 px-4 text-center"><span className="text-xl font-black text-orange-600">%14</span></td>
                  <td className="py-3 px-4"><span className="inline-block px-2 py-0.5 bg-emerald-50 text-emerald-600 text-[10px] font-bold rounded-full">Q4</span></td>
                  <td className="py-3 px-4 text-xs text-orange-700 font-medium">Black Friday · Cyber Monday · Yılın en yüksek hediye satışı · Discovery set kampanyası · Agresif remarketing</td>
                  <td className="py-3 px-4 text-center">
                    <div className="flex items-center justify-center gap-0.5">
                      <div className="w-2.5 h-2.5 rounded-full bg-orange-500"></div>
                      <div className="w-2.5 h-2.5 rounded-full bg-orange-500"></div>
                      <div className="w-2.5 h-2.5 rounded-full bg-orange-500"></div>
                      <div className="w-2.5 h-2.5 rounded-full bg-orange-500"></div>
                      <div className="w-2.5 h-2.5 rounded-full bg-orange-500"></div>
                    </div>
                  </td>
                </tr>

                <tr className="border-b border-gray-200 bg-gray-50/30">
                  <td className="py-3 px-4 font-bold text-gray-900">Aralık</td>
                  <td className="py-3 px-4 text-center"><span className="text-base font-black text-red-600">%10</span></td>
                  <td className="py-3 px-4"><span className="inline-block px-2 py-0.5 bg-emerald-50 text-emerald-600 text-[10px] font-bold rounded-full">Q4</span></td>
                  <td className="py-3 px-4 text-xs text-gray-600">Yılbaşı hediye peak (parfüm #1 hediye) · Kış koleksiyonu · Son dakika hediye push · Premium paketleme vurgusu</td>
                  <td className="py-3 px-4 text-center">
                    <div className="flex items-center justify-center gap-0.5">
                      <div className="w-2 h-2 rounded-full bg-red-500"></div>
                      <div className="w-2 h-2 rounded-full bg-red-500"></div>
                      <div className="w-2 h-2 rounded-full bg-red-500"></div>
                      <div className="w-2 h-2 rounded-full bg-red-500"></div>
                      <div className="w-2 h-2 rounded-full bg-red-500"></div>
                    </div>
                  </td>
                </tr>

              </tbody>
              <tfoot>
                <tr className="bg-gray-900 text-white">
                  <td className="py-3 px-4 font-bold rounded-bl-lg">TOPLAM</td>
                  <td className="py-3 px-4 text-center font-black text-xl">%100</td>
                  <td className="py-3 px-4" colSpan="2"><span className="text-xs text-neutral-400">Q1 %26 · Q2 %20 · Q3 %21 · Q4 %33</span></td>
                  <td className="py-3 px-4 rounded-br-lg"></td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Quarterly Summary */}
          <div className="mt-6 grid grid-cols-4 gap-3">
            <div className="rounded-xl bg-blue-50 border border-blue-100 p-4 text-center">
              <p className="text-[10px] font-bold text-blue-400 uppercase tracking-wider">Q1 — Oca / Şub / Mar</p>
              <p className="text-2xl font-black text-blue-700 mt-1">%26</p>
              <p className="text-[10px] text-blue-500 mt-1">Sevgililer Günü + Yeni Yıl artığı</p>
            </div>
            <div className="rounded-xl bg-violet-50 border border-violet-100 p-4 text-center">
              <p className="text-[10px] font-bold text-violet-400 uppercase tracking-wider">Q2 — Nis / May / Haz</p>
              <p className="text-2xl font-black text-violet-700 mt-1">%20</p>
              <p className="text-[10px] text-violet-500 mt-1">Ara dönem + Geçiş</p>
            </div>
            <div className="rounded-xl bg-amber-50 border border-amber-100 p-4 text-center">
              <p className="text-[10px] font-bold text-amber-400 uppercase tracking-wider">Q3 — Tem / Ağu / Eyl</p>
              <p className="text-2xl font-black text-amber-700 mt-1">%21</p>
              <p className="text-[10px] text-amber-500 mt-1">Düşük sezon → Back to School</p>
            </div>
            <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-4 text-center">
              <p className="text-[10px] font-bold text-emerald-400 uppercase tracking-wider">Q4 — Eki / Kas / Ara</p>
              <p className="text-2xl font-black text-emerald-700 mt-1">%33</p>
              <p className="text-[10px] text-emerald-500 mt-1">Peak Season + Black Friday</p>
            </div>
          </div>

        </div>
      </div>

      {/* ───────── Ajans Yaklaşımı & Stratejik Sorular ───────── */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-bold text-gray-900">Ajans Yaklaşımı &mdash; Stratejik İş Ortaklığı</h2>
          <p className="text-xs text-gray-400 mt-1">Bütçe yönetiminde yalnızca operasyon değil; sorgulayan, öneri getiren ve uzun vadede markayla birlikte büyümeyi hedefleyen bir iş ortağı olarak konumlanıyoruz</p>
        </div>
        <div className="p-6">

          {/* Manifesto */}
          <div className="rounded-xl bg-gray-900 text-white p-6 mb-8">
            <p className="text-sm leading-relaxed text-neutral-300">
              Şeffaf iletişim, düzenli ve yorum içeren raporlama ve <strong className="text-white">&quot;ne oldu?&quot;</strong> sorusunun ötesinde <strong className="text-white">&quot;neden oldu ve nasıl ilerlemeliyiz?&quot;</strong> perspektifi bizim için temel yaklaşımdır. Aşağıdaki sorular, her raporlama döngüsünde ve stratejik toplantıda ajans olarak aktif şekilde sorguladığımız konulardır.
            </p>
          </div>

          {/* Soru Kategorileri */}
          <div className="space-y-6">

            {/* 1 — Performans & Verimlilik */}
            <div className="rounded-xl border border-blue-100 overflow-hidden">
              <div className="bg-blue-50 px-5 py-3 flex items-center gap-3">
                <span className="w-8 h-8 rounded-lg bg-blue-600 text-white flex items-center justify-center text-xs font-black">01</span>
                <div>
                  <h3 className="text-sm font-bold text-gray-900">Performans &amp; Verimlilik Sorguları</h3>
                  <p className="text-[10px] text-gray-500 mt-0.5">Her hafta / aylık rapor döngüsünde</p>
                </div>
              </div>
              <div className="p-5">
                <ul className="space-y-3">
                  {[
                    ['CPC neden bu ay yükseldi?', 'Rakip aktivitesi mi, sezonsal talep artışı mı, yoksa bid strategy kaynaklı mı? Kuru hesaba katınca dolar bazında da yükseldi mi?'],
                    ['Hangi kampanya tipi en düşük CPA ile dönüşüm getiriyor?', 'Search vs PMAX vs Shopping performans karşılaştırması — bütçe kaydırma önerisi var mı?'],
                    ['ROAS hedefinden sapma var mı, varsa nedeni ne?', 'Kreatif yorgunluğu mu, hedef kitle doygunluğu mu, yoksa ürün karması mı etkiliyor?'],
                    ['Meta ve Google arasındaki bütçe dağılımı optimal mi?', 'Marginal ROAS analizi yapıldığında hangi platforma ek bütçe kaydırılmalı?'],
                    ['Dolar bazında maliyetlerimiz geçen aya göre ne durumda?', 'TL\'deki artış gerçek bir maliyet artışı mı yoksa kur etkisi mi? Net performans değişimi nedir?'],
                  ].map(([q, detail], i) => (
                    <li key={i} className="flex gap-3">
                      <span className="mt-0.5 w-5 h-5 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-[10px] font-bold flex-shrink-0">?</span>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{q}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{detail}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* 2 — Hedef Kitle & Segmentasyon */}
            <div className="rounded-xl border border-emerald-100 overflow-hidden">
              <div className="bg-emerald-50 px-5 py-3 flex items-center gap-3">
                <span className="w-8 h-8 rounded-lg bg-emerald-600 text-white flex items-center justify-center text-xs font-black">02</span>
                <div>
                  <h3 className="text-sm font-bold text-gray-900">Hedef Kitle &amp; Segmentasyon Sorguları</h3>
                  <p className="text-[10px] text-gray-500 mt-0.5">Aylık / çeyreklik strateji toplantılarında</p>
                </div>
              </div>
              <div className="p-5">
                <ul className="space-y-3">
                  {[
                    ['Lookalike audience\'lar hâlâ performans gösteriyor mu?', 'Seed listemiz ne zaman güncellendi? Purchase-based seed vs Add-to-cart seed karşılaştırması yapıldı mı?'],
                    ['Sepet terk segmentinde recovery oranımız kaç?', 'E-posta + DPA + RTB House birlikte çalışıyor mu, yoksa çakışma (overlap) mı var?'],
                    ['Yeni müşteri kazanım maliyeti (nCAC) ile tekrar müşteri maliyeti arasındaki fark nedir?', 'Bütçenin ne kadarı yeni müşteriye, ne kadarı mevcut müşteriye gidiyor — bu oran doğru mu?'],
                    ['Hangi persona en yüksek CLV üretiyor?', 'Koleksiyoner mi, hediye alıcısı mı, discovery set ile giren mi? Buna göre bütçe ağırlığı değişmeli mi?'],
                    ['Coğrafi segmentasyonda kaçırdığımız fırsat var mı?', 'İstanbul dışı şehirlerde (Ankara, İzmir, Antalya) potansiyel büyüme alanı test edildi mi?'],
                  ].map(([q, detail], i) => (
                    <li key={i} className="flex gap-3">
                      <span className="mt-0.5 w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-[10px] font-bold flex-shrink-0">?</span>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{q}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{detail}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* 3 — Kreatif & İçerik */}
            <div className="rounded-xl border border-violet-100 overflow-hidden">
              <div className="bg-violet-50 px-5 py-3 flex items-center gap-3">
                <span className="w-8 h-8 rounded-lg bg-violet-600 text-white flex items-center justify-center text-xs font-black">03</span>
                <div>
                  <h3 className="text-sm font-bold text-gray-900">Kreatif &amp; İçerik Sorguları</h3>
                  <p className="text-[10px] text-gray-500 mt-0.5">Her kampanya döngüsü öncesi ve sonrası</p>
                </div>
              </div>
              <div className="p-5">
                <ul className="space-y-3">
                  {[
                    ['Hangi kreatif format en iyi CTR ve CVR üretiyor?', 'Statik vs video vs carousel — platform bazında farklılık var mı? TikTok\'ta UGC, Meta\'da katalog mu kazanıyor?'],
                    ['Kreatif yorgunluğu (ad fatigue) başladı mı?', 'Frequency 3+ olan kampanyalarda CTR düşüşü gözlemleniyor mu? Yeni kreatif rotasyonu planı ne?'],
                    ['Ürün görselleri mi yoksa lifestyle görselleri mi daha çok dönüştürüyor?', 'A/B test sonuçları ne diyor? Funnel aşamasına göre farklılık var mı?'],
                    ['Landing page deneyimi reklam vaadiyle tutarlı mı?', 'Reklam mesajı ile açılış sayfası arasında kopukluk var mı? Bounce rate ve session duration buna işaret ediyor mu?'],
                  ].map(([q, detail], i) => (
                    <li key={i} className="flex gap-3">
                      <span className="mt-0.5 w-5 h-5 rounded-full bg-violet-100 text-violet-700 flex items-center justify-center text-[10px] font-bold flex-shrink-0">?</span>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{q}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{detail}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* 4 — Bütçe & Planlama */}
            <div className="rounded-xl border border-amber-100 overflow-hidden">
              <div className="bg-amber-50 px-5 py-3 flex items-center gap-3">
                <span className="w-8 h-8 rounded-lg bg-amber-600 text-white flex items-center justify-center text-xs font-black">04</span>
                <div>
                  <h3 className="text-sm font-bold text-gray-900">Bütçe &amp; Kaynak Planlama Sorguları</h3>
                  <p className="text-[10px] text-gray-500 mt-0.5">Aylık bütçe review toplantılarında</p>
                </div>
              </div>
              <div className="p-5">
                <ul className="space-y-3">
                  {[
                    ['Bu ayın bütçesi planlandığı gibi mi harcandı, sapma varsa neden?', 'Pacing doğru mu? Ay ortasında agresif harcama mı yapıldı yoksa ay sonuna mı yığıldı?'],
                    ['Black Friday / kampanya dönemlerine kaç hafta kaldı, pre-heat bütçesi ayrıldı mı?', 'Geçen yılın kampanya verileri benchmark olarak kullanılıyor mu?'],
                    ['Branding ve Performance arasındaki %35-%65 dağılım hâlâ doğru mu?', 'Marka bilinirliği hedeflerine ulaşıldıysa performansa mı kaydırmalı, yoksa branding awareness hâlâ yetersiz mi?'],
                    ['Yeni bir platform testi (Pinterest, Programmatic) için pilot bütçe ayırdık mı?', 'Mevcut kanallardaki diminishing returns sinyalleri var mı? Diversifikasyon zamanı geldi mi?'],
                    ['Sezonsal bütçe artırımı için hangi sinyaller bekleniyor?', 'Google Trends, site trafiği, sepet verileri — erken sezon sinyali yakaladık mı?'],
                  ].map(([q, detail], i) => (
                    <li key={i} className="flex gap-3">
                      <span className="mt-0.5 w-5 h-5 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-[10px] font-bold flex-shrink-0">?</span>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{q}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{detail}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* 5 — Rekabet & Pazar */}
            <div className="rounded-xl border border-red-100 overflow-hidden">
              <div className="bg-red-50 px-5 py-3 flex items-center gap-3">
                <span className="w-8 h-8 rounded-lg bg-red-600 text-white flex items-center justify-center text-xs font-black">05</span>
                <div>
                  <h3 className="text-sm font-bold text-gray-900">Rekabet &amp; Pazar Sorguları</h3>
                  <p className="text-[10px] text-gray-500 mt-0.5">Çeyreklik strateji değerlendirmelerinde</p>
                </div>
              </div>
              <div className="p-5">
                <ul className="space-y-3">
                  {[
                    ['Rakipler hangi kanallarda agresifleşti?', 'Auction Insights / Meta Ad Library verilerine göre Nishane, Byredo, Atelier Rebul son dönemde ne yapıyor?'],
                    ['Rakip CPC artışları bizim maliyetlerimizi etkiliyor mu?', 'Impression share düşüşü var mı? Outranking share değişimi ne yönde?'],
                    ['Nishane ve diğer Türk niş markaların dijital stratejileri ne?', 'Aynı keyword\'lerde rakiplerle rekabet mi ediyoruz? Conquest fırsatları nerede?'],
                    ['Sektörde yeni bir oyuncu veya trend belirdi mi?', 'Yeni açılan niş e-ticaret siteleri, değişen tüketici davranışları, yeni platform fırsatları?'],
                  ].map(([q, detail], i) => (
                    <li key={i} className="flex gap-3">
                      <span className="mt-0.5 w-5 h-5 rounded-full bg-red-100 text-red-700 flex items-center justify-center text-[10px] font-bold flex-shrink-0">?</span>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{q}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{detail}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* 6 — Uzun Vadeli Büyüme */}
            <div className="rounded-xl border border-gray-200 overflow-hidden">
              <div className="bg-gray-100 px-5 py-3 flex items-center gap-3">
                <span className="w-8 h-8 rounded-lg bg-gray-900 text-white flex items-center justify-center text-xs font-black">06</span>
                <div>
                  <h3 className="text-sm font-bold text-gray-900">Uzun Vadeli Büyüme &amp; Vizyon Sorguları</h3>
                  <p className="text-[10px] text-gray-500 mt-0.5">Yıllık / yarı yıllık stratejik planlama</p>
                </div>
              </div>
              <div className="p-5">
                <ul className="space-y-3">
                  {[
                    ['1st-party data stratejimiz ne aşamada?', 'E-posta listesi büyüme hızı, SMS opt-in oranları, cookie-less dünyaya hazırlık seviyesi nedir?'],
                    ['Marka bilinirliği ölçümü (brand lift study) yaptık mı?', 'Aided / unaided awareness metrikleri nerede? Branding yatırımlarının geri dönüşü nasıl ölçülüyor?'],
                    ['Organik trafik payımız artıyor mu, yoksa paid\'e bağımlılık mı büyüyor?', 'SEO ve içerik yatırımları paid bütçe baskısını hafifletiyor mu?'],
                    ['Customer Lifetime Value (CLV) trendi nasıl?', '6 ay önce kazanılan müşterilerin tekrar satın alma oranı nedir? CLV/CAC oranı sağlıklı mı?'],
                    ['Yılsonu hedeflerine ulaşmak için kalan dönemde ne yapılmalı?', 'Gap analizi — hedefe ne kadar uzağız, hangi kaldıraçları kullanabiliriz?'],
                  ].map(([q, detail], i) => (
                    <li key={i} className="flex gap-3">
                      <span className="mt-0.5 w-5 h-5 rounded-full bg-gray-200 text-gray-700 flex items-center justify-center text-[10px] font-bold flex-shrink-0">?</span>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{q}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{detail}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* ───────── Ajans Çalışma Yapısı ───────── */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-bold text-gray-900">Ajans Çalışma Yapısı</h2>
          <p className="text-xs text-gray-400 mt-1">Şeffaf, ölçülebilir ve sistematik bir operasyon modeli — her gün, her hafta, her ay</p>
        </div>
        <div className="p-6">

          {/* Üst Özet Kartları */}
          <div className="grid grid-cols-4 gap-4 mb-8">
            <div className="rounded-xl bg-blue-50 border border-blue-100 p-4 text-center">
              <p className="text-2xl font-black text-blue-700">Kanban</p>
              <p className="text-[10px] text-blue-500 mt-1 font-medium">Task Board üzerinden tüm işler takip edilir</p>
            </div>
            <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-4 text-center">
              <p className="text-2xl font-black text-emerald-700">Checklist</p>
              <p className="text-[10px] text-emerald-500 mt-1 font-medium">Her güne kontrol listesi ile başlanır</p>
            </div>
            <div className="rounded-xl bg-amber-50 border border-amber-100 p-4 text-center">
              <p className="text-2xl font-black text-amber-700">Timesheet</p>
              <p className="text-[10px] text-amber-500 mt-1 font-medium">Kimin hangi markaya ne kadar vakit ayırdığı yazılı</p>
            </div>
            <div className="rounded-xl bg-violet-50 border border-violet-100 p-4 text-center">
              <p className="text-2xl font-black text-violet-700">Review</p>
              <p className="text-[10px] text-violet-500 mt-1 font-medium">Haftalık geliştirme önerileri içeride sorgulanır</p>
            </div>
          </div>

          {/* Günlük / Haftalık / Aylık Döngü */}
          <div className="space-y-6">

            {/* Günlük */}
            <div className="rounded-xl border border-blue-200 overflow-hidden">
              <div className="bg-blue-600 text-white px-5 py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center text-xs font-black">D</span>
                  <h3 className="text-sm font-bold">Günlük Döngü — Daily Ops</h3>
                </div>
                <span className="text-[10px] bg-white/20 px-2.5 py-1 rounded-full font-medium">Her iş günü</span>
              </div>
              <div className="p-5">
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-3">Sabah Checklist (09:00)</p>
                    <ul className="space-y-2">
                      {[
                        'Kampanya durumları kontrol (active / paused / error)',
                        'Bütçe pacing kontrol — günlük harcama hedefte mi?',
                        'Anomali taraması — CPC, CTR, CVR\'da ani sapma var mı?',
                        'Disapproved reklam kontrolü',
                        'Rakip aktivite hızlı tarama (Auction Insights)',
                        'Stok / ürün feed hatası kontrolü (Shopping / DPA)',
                      ].map((item, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <span className="mt-1 w-4 h-4 rounded bg-blue-100 flex items-center justify-center flex-shrink-0">
                            <span className="w-1.5 h-1.5 rounded-sm bg-blue-600"></span>
                          </span>
                          <span className="text-xs text-gray-700">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-3">Akşam Kapanış (17:30)</p>
                    <ul className="space-y-2">
                      {[
                        'Günlük harcama ve dönüşüm özeti log\'a işlenir',
                        'Kritik anomali varsa marka yöneticisine bildirim',
                        'Ertesi gün için task board güncelleme',
                        'A/B test sonuçları kontrol (yeterli data birikti mi?)',
                      ].map((item, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <span className="mt-1 w-4 h-4 rounded bg-blue-100 flex items-center justify-center flex-shrink-0">
                            <span className="w-1.5 h-1.5 rounded-sm bg-blue-600"></span>
                          </span>
                          <span className="text-xs text-gray-700">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Haftalık */}
            <div className="rounded-xl border border-emerald-200 overflow-hidden">
              <div className="bg-emerald-600 text-white px-5 py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center text-xs font-black">W</span>
                  <h3 className="text-sm font-bold">Haftalık Döngü — Weekly Sprint</h3>
                </div>
                <span className="text-[10px] bg-white/20 px-2.5 py-1 rounded-full font-medium">Her Pazartesi + Cuma</span>
              </div>
              <div className="p-5">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-2 px-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Gün</th>
                        <th className="text-left py-2 px-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Aktivite</th>
                        <th className="text-left py-2 px-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Çıktı</th>
                      </tr>
                    </thead>
                    <tbody className="text-gray-700">
                      {[
                        ['Pazartesi', 'Sprint planlama — haftalık hedefler, task atama, önceliklendirme', 'Kanban board güncellenir, sorumlular netleşir'],
                        ['Salı-Perşembe', 'Geliştirme çalışmaları — yeni kampanya kurulumu, A/B test, bid optimizasyon, kreatif yenileme', 'Her task board\'da ilerleme güncellenir'],
                        ['Çarşamba', 'İç sorgulama toplantısı — her marka için geliştirme önerileri masaya yatırılır', 'Onaylanan öneriler sprint\'e eklenir'],
                        ['Cuma', 'Haftalık performans review — veri analizi, sapma tespiti, aksiyon listesi', 'Haftalık mini rapor + sonraki hafta aksiyonları'],
                      ].map(([gun, aktivite, cikti], i) => (
                        <tr key={i} className={`border-b border-gray-100 ${i % 2 === 1 ? 'bg-gray-50/30' : ''}`}>
                          <td className="py-3 px-3 font-bold text-gray-900 whitespace-nowrap">{gun}</td>
                          <td className="py-3 px-3 text-xs text-gray-600">{aktivite}</td>
                          <td className="py-3 px-3 text-xs text-gray-500">{cikti}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Aylık */}
            <div className="rounded-xl border border-amber-200 overflow-hidden">
              <div className="bg-amber-600 text-white px-5 py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center text-xs font-black">M</span>
                  <h3 className="text-sm font-bold">Aylık Döngü — Monthly Strategy</h3>
                </div>
                <span className="text-[10px] bg-white/20 px-2.5 py-1 rounded-full font-medium">Her ayın ilk haftası</span>
              </div>
              <div className="p-5">
                <ul className="space-y-3">
                  {[
                    ['Aylık Performans Raporu', 'Detaylı veri analizi, TL ve USD bazında karşılaştırma, önceki ay ve geçen yıl aynı ay benchmark\'ı'],
                    ['Bütçe Review', 'Planlanan vs gerçekleşen harcama, platform bazında sapma analizi, sonraki ay bütçe önerisi'],
                    ['Strateji Toplantısı (Marka ile)', 'Performans sunumu, öneriler, marka tarafı feedback, sonraki ay hedefleri ve aksiyon planı'],
                    ['Geliştirme Backlog Güncelleme', 'Test sonuçlarından çıkarımlar, yeni fırsatlar, teknik iyileştirmeler — öncelik sıralaması'],
                    ['Timesheet Özeti', 'Ekibin markaya ayırdığı toplam saat, görev dağılımı, verimlilik değerlendirmesi'],
                  ].map(([title, desc], i) => (
                    <li key={i} className="flex gap-3">
                      <span className="mt-0.5 w-6 h-6 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center text-[10px] font-bold flex-shrink-0">{i + 1}</span>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{title}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{desc}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Kanban Board Örneği */}
            <div className="rounded-xl border border-gray-200 overflow-hidden">
              <div className="bg-gray-900 text-white px-5 py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center text-xs font-black">KB</span>
                  <h3 className="text-sm font-bold">Kanban Board — Task Akışı</h3>
                </div>
                <span className="text-[10px] bg-white/20 px-2.5 py-1 rounded-full font-medium">Canlı takip</span>
              </div>
              <div className="p-5">
                <div className="grid grid-cols-4 gap-4">
                  {[
                    { title: 'Backlog', color: 'bg-gray-100 border-gray-200', titleColor: 'text-gray-600', items: ['Pinterest pilot kampanya kurulumu', 'Yeni landing page A/B testi', 'Q2 kreatif brief hazırlığı'] },
                    { title: 'In Progress', color: 'bg-blue-50 border-blue-200', titleColor: 'text-blue-700', items: ['Google PMAX asset güncelleme', 'Meta DPA feed optimizasyonu', 'RTB House segment kurulumu'] },
                    { title: 'Review', color: 'bg-amber-50 border-amber-200', titleColor: 'text-amber-700', items: ['Haftalık performans raporu', 'Yeni keyword seti onayı', 'Branding video kreatif review'] },
                    { title: 'Done', color: 'bg-emerald-50 border-emerald-200', titleColor: 'text-emerald-700', items: ['Black Friday kampanya kurulumu', 'Sepet terk e-posta akışı', 'Search conquest kampanyaları'] },
                  ].map((col, i) => (
                    <div key={i} className={`rounded-lg border ${col.color} p-3`}>
                      <p className={`text-[10px] font-bold uppercase tracking-wider mb-3 ${col.titleColor}`}>{col.title}</p>
                      <div className="space-y-2">
                        {col.items.map((item, j) => (
                          <div key={j} className="bg-white rounded-md border border-gray-200 px-3 py-2 shadow-sm">
                            <p className="text-[11px] text-gray-700">{item}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Ekip Zaman Dağılımı */}
            <div className="rounded-xl border border-gray-200 overflow-hidden">
              <div className="bg-gray-100 px-5 py-3 flex items-center gap-3">
                <span className="w-8 h-8 rounded-lg bg-gray-900 text-white flex items-center justify-center text-xs font-black">T</span>
                <div>
                  <h3 className="text-sm font-bold text-gray-900">Haftalık Timesheet — Zaman Dağılımı</h3>
                  <p className="text-[10px] text-gray-500 mt-0.5">Kimin hangi markaya ne kadar vakit ayırdığı kayıt altında</p>
                </div>
              </div>
              <div className="p-5">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b-2 border-gray-900">
                        <th className="text-left py-2 px-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Görev Alanı</th>
                        <th className="text-center py-2 px-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Haftalık Saat</th>
                        <th className="text-center py-2 px-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Pay</th>
                        <th className="text-left py-2 px-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Açıklama</th>
                      </tr>
                    </thead>
                    <tbody className="text-gray-700">
                      {[
                        ['Günlük Kontrol & Monitoring', '5s', '%25', 'Sabah checklist, anomali tarama, bütçe pacing'],
                        ['Kampanya Optimizasyon', '6s', '%30', 'Bid ayarları, hedef kitle güncelleme, A/B test yönetimi'],
                        ['Analiz & Raporlama', '3s', '%15', 'Veri analizi, haftalık/aylık rapor hazırlığı'],
                        ['Strateji & Planlama', '2s', '%10', 'Yeni kampanya planı, test önerisi, bütçe review'],
                        ['Kreatif Koordinasyon', '2s', '%10', 'Brief hazırlama, kreatif review, asset yönetimi'],
                        ['İletişim & Toplantı', '2s', '%10', 'Marka toplantısı, iç sorgulama, slack/mail iletişimi'],
                      ].map(([alan, saat, pay, aciklama], i) => (
                        <tr key={i} className={`border-b border-gray-100 ${i % 2 === 1 ? 'bg-gray-50/30' : ''}`}>
                          <td className="py-3 px-3 font-bold text-gray-900">{alan}</td>
                          <td className="py-3 px-3 text-center font-bold">{saat}</td>
                          <td className="py-3 px-3 text-center">
                            <span className="inline-block px-2 py-0.5 bg-gray-900 text-white text-[10px] font-bold rounded-full">{pay}</span>
                          </td>
                          <td className="py-3 px-3 text-xs text-gray-500">{aciklama}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-gray-900 text-white">
                        <td className="py-3 px-3 font-bold rounded-bl-lg">TOPLAM</td>
                        <td className="py-3 px-3 text-center font-black text-lg">20s</td>
                        <td className="py-3 px-3 text-center font-black">%100</td>
                        <td className="py-3 px-3 text-xs text-neutral-400 rounded-br-lg">Haftalık marka bazlı ayrılmış zaman</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* ───────── 2026 Yıllık Projeksiyon ───────── */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-bold text-gray-900">2026 Yıllık Projeksiyon — Estimation</h2>
          <p className="text-xs text-gray-400 mt-1">Session, Conversion Rate ve AOV bazlı 12 aylık gelir projeksiyonu</p>
        </div>
        <div className="p-6">

          {/* Üst Özet Kartları */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            {[
              { label: 'Session Aralığı', value: '50K → 400K', color: 'blue', sub: 'Eylül-Kasım pik, Aralık hafif düşüş' },
              { label: 'CR Aralığı', value: '%0.80 → %0.34', color: 'emerald', sub: 'Trafik arttıkça doğal düşüş' },
              { label: 'AOV Aralığı', value: '₺7,000 → ₺9,500', color: 'amber', sub: 'Sepet değeri kademeli artış' },
              { label: 'Tahmini Yıllık Gelir', value: (() => {
                const d = [
                  { s: 50000, cr: 0.0080, aov: 7000 },
                  { s: 60000, cr: 0.0075, aov: 7200 },
                  { s: 75000, cr: 0.0072, aov: 7400 },
                  { s: 90000, cr: 0.0068, aov: 7500 },
                  { s: 110000, cr: 0.0065, aov: 7700 },
                  { s: 130000, cr: 0.0062, aov: 7900 },
                  { s: 150000, cr: 0.0058, aov: 8100 },
                  { s: 180000, cr: 0.0054, aov: 8300 },
                  { s: 280000, cr: 0.0046, aov: 8500 },
                  { s: 320000, cr: 0.0042, aov: 8800 },
                  { s: 400000, cr: 0.0036, aov: 9200 },
                  { s: 350000, cr: 0.0034, aov: 9500 },
                ];
                const total = d.reduce((sum, m) => sum + Math.round(m.s * m.cr) * m.aov, 0);
                return `₺${(total / 1000000).toFixed(0)}M`;
              })(), color: 'violet', sub: 'Kümülatif ciro hedefi' },
            ].map((card, i) => (
              <div key={i} className={`rounded-xl bg-${card.color}-50 border border-${card.color}-100 p-4 text-center`}>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">{card.label}</p>
                <p className={`text-xl font-black text-${card.color}-700 mt-1`}>{card.value}</p>
                <p className={`text-[10px] text-${card.color}-500 mt-1`}>{card.sub}</p>
              </div>
            ))}
          </div>

          {/* Projeksiyon Tablosu */}
          {(() => {
            const months = [
              { name: 'Ocak', sessions: 50000, cr: 0.0080, aov: 7000, quarter: 'Q1' },
              { name: 'Şubat', sessions: 60000, cr: 0.0075, aov: 7200, quarter: 'Q1' },
              { name: 'Mart', sessions: 75000, cr: 0.0072, aov: 7400, quarter: 'Q1' },
              { name: 'Nisan', sessions: 90000, cr: 0.0068, aov: 7500, quarter: 'Q2' },
              { name: 'Mayıs', sessions: 110000, cr: 0.0065, aov: 7700, quarter: 'Q2' },
              { name: 'Haziran', sessions: 130000, cr: 0.0062, aov: 7900, quarter: 'Q2' },
              { name: 'Temmuz', sessions: 150000, cr: 0.0058, aov: 8100, quarter: 'Q3' },
              { name: 'Ağustos', sessions: 180000, cr: 0.0054, aov: 8300, quarter: 'Q3' },
              { name: 'Eylül', sessions: 280000, cr: 0.0046, aov: 8500, quarter: 'Q3' },
              { name: 'Ekim', sessions: 320000, cr: 0.0042, aov: 8800, quarter: 'Q4' },
              { name: 'Kasım', sessions: 400000, cr: 0.0036, aov: 9200, quarter: 'Q4' },
              { name: 'Aralık', sessions: 350000, cr: 0.0034, aov: 9500, quarter: 'Q4' },
            ].map(m => ({
              ...m,
              transactions: Math.round(m.sessions * m.cr),
              revenue: Math.round(m.sessions * m.cr) * m.aov,
            }));

            const totalSessions = months.reduce((s, m) => s + m.sessions, 0);
            const totalTransactions = months.reduce((s, m) => s + m.transactions, 0);
            const totalRevenue = months.reduce((s, m) => s + m.revenue, 0);
            const avgCr = totalTransactions / totalSessions;
            const avgAov = totalRevenue / totalTransactions;
            const maxRevenue = Math.max(...months.map(m => m.revenue));

            const quarterColors = { Q1: 'bg-blue-50 text-blue-700', Q2: 'bg-emerald-50 text-emerald-700', Q3: 'bg-amber-50 text-amber-700', Q4: 'bg-violet-50 text-violet-700' };

            return (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-900 text-white">
                      <th className="py-3 px-3 text-left text-[10px] font-bold uppercase tracking-wider rounded-tl-lg">Ay</th>
                      <th className="py-3 px-3 text-center text-[10px] font-bold uppercase tracking-wider">Q</th>
                      <th className="py-3 px-3 text-right text-[10px] font-bold uppercase tracking-wider">Session</th>
                      <th className="py-3 px-3 text-right text-[10px] font-bold uppercase tracking-wider">CR</th>
                      <th className="py-3 px-3 text-right text-[10px] font-bold uppercase tracking-wider">AOV (₺)</th>
                      <th className="py-3 px-3 text-right text-[10px] font-bold uppercase tracking-wider">İşlem</th>
                      <th className="py-3 px-3 text-right text-[10px] font-bold uppercase tracking-wider">Gelir (₺)</th>
                      <th className="py-3 px-3 text-left text-[10px] font-bold uppercase tracking-wider rounded-tr-lg" style={{ minWidth: '200px' }}>Gelir Dağılımı</th>
                    </tr>
                  </thead>
                  <tbody>
                    {months.map((m, i) => {
                      const isQuarterEnd = i === 2 || i === 5 || i === 8 || i === 11;
                      const isQuarterStart = i === 0 || i === 3 || i === 6 || i === 9;
                      const quarterMonths = months.filter(x => x.quarter === m.quarter);
                      const quarterRevenue = quarterMonths.reduce((s, x) => s + x.revenue, 0);

                      return (
                        <React.Fragment key={i}>
                          <tr className={`border-b ${isQuarterEnd ? 'border-gray-300' : 'border-gray-100'} ${i % 2 === 1 ? 'bg-gray-50/30' : ''}`}>
                            <td className="py-3 px-3 font-bold text-gray-900">{m.name}</td>
                            <td className="py-3 px-3 text-center">
                              <span className={`inline-block px-2 py-0.5 text-[10px] font-bold rounded-full ${quarterColors[m.quarter]}`}>{m.quarter}</span>
                            </td>
                            <td className="py-3 px-3 text-right font-mono text-xs">{m.sessions.toLocaleString('tr-TR')}</td>
                            <td className="py-3 px-3 text-right font-mono text-xs font-semibold text-emerald-700">%{(m.cr * 100).toFixed(2)}</td>
                            <td className="py-3 px-3 text-right font-mono text-xs">₺{m.aov.toLocaleString('tr-TR')}</td>
                            <td className="py-3 px-3 text-right font-mono text-xs font-bold">{m.transactions.toLocaleString('tr-TR')}</td>
                            <td className="py-3 px-3 text-right font-mono text-xs font-bold text-violet-700">₺{(m.revenue / 1000000).toFixed(1)}M</td>
                            <td className="py-3 px-3">
                              <div className="flex items-center gap-2">
                                <div className="flex-1 bg-gray-100 rounded-full h-3 overflow-hidden">
                                  <div
                                    className="h-full rounded-full bg-gradient-to-r from-violet-500 to-violet-600"
                                    style={{ width: `${(m.revenue / maxRevenue * 100).toFixed(1)}%` }}
                                  ></div>
                                </div>
                                <span className="text-[10px] text-gray-400 font-mono w-10 text-right">{(m.revenue / totalRevenue * 100).toFixed(1)}%</span>
                              </div>
                            </td>
                          </tr>
                          {isQuarterEnd && (() => {
                            const qSessions = quarterMonths.reduce((s, x) => s + x.sessions, 0);
                            const qTransactions = quarterMonths.reduce((s, x) => s + x.transactions, 0);
                            const qAvgCr = qTransactions / qSessions;
                            return (
                              <tr className="bg-gray-100 border-b-2 border-gray-300">
                                <td className="py-2 px-3 text-[10px] font-bold text-gray-500 uppercase">{m.quarter} Toplam</td>
                                <td></td>
                                <td className="py-2 px-3 text-right text-[10px] font-bold text-gray-600">{qSessions.toLocaleString('tr-TR')}</td>
                                <td className="py-2 px-3 text-right text-[10px] font-bold text-emerald-600">%{(qAvgCr * 100).toFixed(2)}</td>
                                <td></td>
                                <td className="py-2 px-3 text-right text-[10px] font-bold text-gray-600">{qTransactions.toLocaleString('tr-TR')}</td>
                                <td className="py-2 px-3 text-right text-[10px] font-black text-gray-700">₺{(quarterRevenue / 1000000).toFixed(1)}M</td>
                                <td className="py-2 px-3 text-[10px] text-gray-400">{(quarterRevenue / totalRevenue * 100).toFixed(1)}% yıllık payı</td>
                              </tr>
                            );
                          })()}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-gray-900 text-white">
                      <td className="py-3 px-3 font-bold rounded-bl-lg">2026 TOPLAM</td>
                      <td></td>
                      <td className="py-3 px-3 text-right font-mono font-bold">{totalSessions.toLocaleString('tr-TR')}</td>
                      <td className="py-3 px-3 text-right font-mono font-bold text-emerald-300">%{(avgCr * 100).toFixed(2)}</td>
                      <td className="py-3 px-3 text-right font-mono font-bold">₺{Math.round(avgAov).toLocaleString('tr-TR')}</td>
                      <td className="py-3 px-3 text-right font-mono font-black text-lg">{totalTransactions.toLocaleString('tr-TR')}</td>
                      <td className="py-3 px-3 text-right font-mono font-black text-lg text-violet-300">₺{(totalRevenue / 1000000).toFixed(0)}M</td>
                      <td className="py-3 px-3 rounded-br-lg"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            );
          })()}

          {/* Alt Not */}
          <div className="mt-4 p-4 bg-amber-50 rounded-xl border border-amber-100">
            <p className="text-xs text-amber-800">
              <span className="font-bold">⚠ Disclaimer:</span> Bu projeksiyon, tanımlanan Session / CR / AOV varsayımlarına dayanmaktadır. 
              Gerçekleşme; reklam bütçesi, kampanya performansı, pazar koşulları, mevsimsellik ve stok durumuna göre değişkenlik gösterebilir. 
              Q4 hedefleri agresif olup Black Friday ve yılbaşı sezonu etkisini içermektedir.
            </p>
          </div>

        </div>
      </div>

      {/* ───────── Mart 2026 Medya Planı — Bütçe & Tahmini Performans ───────── */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="px-6 py-4 border-b flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Mart 2026 Medya Planı</h2>
            <p className="text-xs text-gray-400 mt-1">Google Ads (Search + PMAX + Shopping + YouTube + DG) + Meta Ads (Sales + Reach + Traffic + Video)</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Toplam Bütçe</p>
              <p className="text-2xl font-black text-gray-900">₺400.000</p>
            </div>
            <div className="h-10 w-px bg-gray-200"></div>
            <div className="text-right">
              <p className="text-[10px] font-bold text-emerald-600 uppercase tracking-wider">Performance</p>
              <p className="text-xl font-black text-emerald-700">₺350K</p>
              <p className="text-[10px] text-gray-400">%87,5</p>
            </div>
            <div className="text-right">
              <p className="text-[10px] font-bold text-blue-600 uppercase tracking-wider">Branding</p>
              <p className="text-xl font-black text-blue-700">₺50K</p>
              <p className="text-[10px] text-gray-400">%12,5</p>
            </div>
          </div>
        </div>
        <div className="p-6 space-y-8">

          {/* Campaign Plan Table */}
          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-4 uppercase tracking-wider">Campaign Plan — Régalien Mart 2026</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b-2 border-gray-900 bg-gray-50">
                    <th className="text-left py-3 px-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Platform</th>
                    <th className="text-left py-3 px-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Campaign Type</th>
                    <th className="text-right py-3 px-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Impressions</th>
                    <th className="text-right py-3 px-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Reach</th>
                    <th className="text-right py-3 px-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Clicks</th>
                    <th className="text-right py-3 px-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider">CPC (₺)</th>
                    <th className="text-right py-3 px-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider">CPM (₺)</th>
                    <th className="text-right py-3 px-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Budget</th>
                  </tr>
                </thead>
                <tbody className="text-gray-700">
                  {/* Google Ads */}
                  <tr className="border-b border-gray-200">
                    <td className="py-3 px-3 font-medium text-gray-900" rowSpan="6">
                      <div className="flex items-center gap-2">
                        <img src="/google_ads-512.webp" alt="Google" className="w-5 h-5 object-contain" />
                        Google Ads
                      </div>
                    </td>
                    <td className="py-3 px-3">Search</td>
                    <td className="py-3 px-3 text-right text-gray-500">—</td>
                    <td className="py-3 px-3 text-right text-gray-500">—</td>
                    <td className="py-3 px-3 text-right font-mono">24.800</td>
                    <td className="py-3 px-3 text-right font-mono">₺5,0</td>
                    <td className="py-3 px-3 text-right text-gray-500">—</td>
                    <td className="py-3 px-3 text-right font-bold">₺124.000</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-3 px-3">Performance Max</td>
                    <td className="py-3 px-3 text-right text-gray-500">—</td>
                    <td className="py-3 px-3 text-right text-gray-500">—</td>
                    <td className="py-3 px-3 text-right font-mono">22.756</td>
                    <td className="py-3 px-3 text-right font-mono">₺4,5</td>
                    <td className="py-3 px-3 text-right text-gray-500">—</td>
                    <td className="py-3 px-3 text-right font-bold">₺102.400</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-3 px-3">Shopping</td>
                    <td className="py-3 px-3 text-right text-gray-500">—</td>
                    <td className="py-3 px-3 text-right text-gray-500">—</td>
                    <td className="py-3 px-3 text-right font-mono">16.800</td>
                    <td className="py-3 px-3 text-right font-mono">₺3,0</td>
                    <td className="py-3 px-3 text-right text-gray-500">—</td>
                    <td className="py-3 px-3 text-right font-bold">₺50.400</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-3 px-3">YouTube Video</td>
                    <td className="py-3 px-3 text-right text-gray-500">—</td>
                    <td className="py-3 px-3 text-right text-gray-500">—</td>
                    <td className="py-3 px-3 text-right font-mono">133K views</td>
                    <td className="py-3 px-3 text-right font-mono">₺0,12 CPV</td>
                    <td className="py-3 px-3 text-right text-gray-500">—</td>
                    <td className="py-3 px-3 text-right font-bold">₺16.000</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-3 px-3">Demand Gen</td>
                    <td className="py-3 px-3 text-right font-mono">360.000</td>
                    <td className="py-3 px-3 text-right font-mono">252.000</td>
                    <td className="py-3 px-3 text-right font-mono">1.029</td>
                    <td className="py-3 px-3 text-right font-mono">₺7,0</td>
                    <td className="py-3 px-3 text-right font-mono">₺20,0</td>
                    <td className="py-3 px-3 text-right font-bold">₺7.200</td>
                  </tr>
                  <tr className="border-b-2 border-gray-300 bg-gray-50">
                    <td className="py-3 px-3 font-bold text-gray-900">Google Toplam</td>
                    <td className="py-3 px-3 text-right font-mono font-medium">360.000</td>
                    <td className="py-3 px-3 text-right font-mono font-medium">252.000</td>
                    <td className="py-3 px-3 text-right font-mono font-medium">65.385</td>
                    <td className="py-3 px-3 text-right text-gray-500">—</td>
                    <td className="py-3 px-3 text-right text-gray-500">—</td>
                    <td className="py-3 px-3 text-right font-black text-gray-900">₺300.000</td>
                  </tr>
                  {/* Meta Ads */}
                  <tr className="border-b border-gray-200">
                    <td className="py-3 px-3 font-medium text-gray-900" rowSpan="5">
                      <div className="flex items-center gap-2">
                        <img src="/6033716.png" alt="Meta" className="w-5 h-5 object-contain" />
                        Meta Ads
                      </div>
                    </td>
                    <td className="py-3 px-3">Sales (Advantage+ & Catalog)</td>
                    <td className="py-3 px-3 text-right text-gray-500">—</td>
                    <td className="py-3 px-3 text-right text-gray-500">—</td>
                    <td className="py-3 px-3 text-right font-mono">16.905</td>
                    <td className="py-3 px-3 text-right font-mono">₺4,2</td>
                    <td className="py-3 px-3 text-right text-gray-500">—</td>
                    <td className="py-3 px-3 text-right font-bold">₺71.200</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-3 px-3">Reach & Frequency</td>
                    <td className="py-3 px-3 text-right font-mono">846.154</td>
                    <td className="py-3 px-3 text-right font-mono">592.308</td>
                    <td className="py-3 px-3 text-right text-gray-500">—</td>
                    <td className="py-3 px-3 text-right text-gray-500">—</td>
                    <td className="py-3 px-3 text-right font-mono">₺13,0</td>
                    <td className="py-3 px-3 text-right font-bold">₺11.200</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-3 px-3">Traffic</td>
                    <td className="py-3 px-3 text-right text-gray-500">—</td>
                    <td className="py-3 px-3 text-right text-gray-500">—</td>
                    <td className="py-3 px-3 text-right font-mono">4.480</td>
                    <td className="py-3 px-3 text-right font-mono">₺2,5</td>
                    <td className="py-3 px-3 text-right text-gray-500">—</td>
                    <td className="py-3 px-3 text-right font-bold">₺11.200</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-3 px-3">Video Views</td>
                    <td className="py-3 px-3 text-right text-gray-500">—</td>
                    <td className="py-3 px-3 text-right text-gray-500">—</td>
                    <td className="py-3 px-3 text-right font-mono">87.500 views</td>
                    <td className="py-3 px-3 text-right font-mono">₺0,08 CPV</td>
                    <td className="py-3 px-3 text-right text-gray-500">—</td>
                    <td className="py-3 px-3 text-right font-bold">₺6.400</td>
                  </tr>
                  <tr className="border-b-2 border-gray-300 bg-gray-50">
                    <td className="py-3 px-3 font-bold text-gray-900">Meta Toplam</td>
                    <td className="py-3 px-3 text-right font-mono font-medium">846.154</td>
                    <td className="py-3 px-3 text-right font-mono font-medium">592.308</td>
                    <td className="py-3 px-3 text-right font-mono font-medium">21.385</td>
                    <td className="py-3 px-3 text-right font-mono font-medium">₺3,8</td>
                    <td className="py-3 px-3 text-right text-gray-500">—</td>
                    <td className="py-3 px-3 text-right font-black text-gray-900">₺100.000</td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr className="bg-gray-900 text-white">
                    <td className="py-3 px-3 font-bold rounded-bl-lg" colSpan="2">TOPLAM</td>
                    <td className="py-3 px-3 text-right font-mono font-bold">1.206.154</td>
                    <td className="py-3 px-3 text-right font-mono font-bold">844.308</td>
                    <td className="py-3 px-3 text-right font-mono font-bold">86.770</td>
                    <td className="py-3 px-3 text-right font-mono font-bold">—</td>
                    <td className="py-3 px-3 text-right font-mono font-bold">—</td>
                    <td className="py-3 px-3 text-right font-black text-xl rounded-br-lg">₺400.000</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Spend by Brand */}
          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-4 uppercase tracking-wider">Spend by Collection</h3>
            <p className="text-xs text-gray-400 mb-4">Google Ads & Meta Ads · Koleksiyon bazlı kampanya bütçe dağılımı</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b-2 border-gray-900 bg-gray-50">
                    <th className="text-left py-3 px-4 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Brand</th>
                    <th className="text-right py-3 px-4 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Google Ads</th>
                    <th className="text-right py-3 px-4 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Meta Ads</th>
                    <th className="text-right py-3 px-4 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Total</th>
                    <th className="text-right py-3 px-4 text-[10px] font-bold text-gray-500 uppercase tracking-wider">%</th>
                  </tr>
                </thead>
                <tbody className="text-gray-700">
                  <tr className="border-b border-gray-200">
                    <td className="py-3 px-4 font-bold text-gray-900">Exclusive Collection</td>
                    <td className="py-3 px-4 text-right font-mono">₺120.000</td>
                    <td className="py-3 px-4 text-right font-mono">₺48.000</td>
                    <td className="py-3 px-4 text-right font-bold">₺168.000</td>
                    <td className="py-3 px-4 text-right font-black text-gray-900">42,0%</td>
                  </tr>
                  <tr className="border-b border-gray-200 bg-gray-50/50">
                    <td className="py-3 px-4 font-bold text-gray-900">Heritage Collection</td>
                    <td className="py-3 px-4 text-right font-mono">₺82.500</td>
                    <td className="py-3 px-4 text-right font-mono">₺27.500</td>
                    <td className="py-3 px-4 text-right font-bold">₺110.000</td>
                    <td className="py-3 px-4 text-right font-black text-gray-900">27,5%</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-3 px-4 font-medium text-gray-900">Turkish Bath Rituals</td>
                    <td className="py-3 px-4 text-right font-mono">₺37.500</td>
                    <td className="py-3 px-4 text-right font-mono">₺12.500</td>
                    <td className="py-3 px-4 text-right font-bold">₺50.000</td>
                    <td className="py-3 px-4 text-right font-black text-gray-900">12,5%</td>
                  </tr>
                  <tr className="border-b border-gray-200 bg-gray-50/50">
                    <td className="py-3 px-4 font-medium text-gray-900">Lucky Collection</td>
                    <td className="py-3 px-4 text-right font-mono">₺22.500</td>
                    <td className="py-3 px-4 text-right font-mono">₺7.500</td>
                    <td className="py-3 px-4 text-right font-bold">₺30.000</td>
                    <td className="py-3 px-4 text-right font-black text-gray-900">7,5%</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-3 px-4 font-medium text-gray-900">Discovery Set & Genel Marka</td>
                    <td className="py-3 px-4 text-right font-mono">₺37.500</td>
                    <td className="py-3 px-4 text-right font-mono">₺4.500</td>
                    <td className="py-3 px-4 text-right font-bold">₺42.000</td>
                    <td className="py-3 px-4 text-right font-black text-gray-900">10,5%</td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr className="bg-gray-900 text-white">
                    <td className="py-3 px-4 font-bold rounded-bl-lg">TOPLAM</td>
                    <td className="py-3 px-4 text-right font-mono font-bold">₺300.000</td>
                    <td className="py-3 px-4 text-right font-mono font-bold">₺100.000</td>
                    <td className="py-3 px-4 text-right font-black text-xl rounded-br-lg">₺400.000</td>
                    <td className="py-3 px-4 text-right font-black">100%</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Hedeflenen Ciro ve Trafik — Ay sonu tahmini */}
          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-4 uppercase tracking-wider">Hedeflenen Ciro ve Trafik</h3>
            <p className="text-xs text-gray-400 mb-4">GA verilerine dayalı ay sonu projeksiyonu · AOV ve CR trendi dikkate alınır</p>
            {!brandId ? (
              <p className="text-sm text-amber-600 py-4">Sol menüden Régalien markasını seçerek verileri görüntüleyebilirsiniz.</p>
            ) : monthly.isLoading ? (
              <div className="flex justify-center py-8"><div className="animate-pulse text-gray-400 text-sm">Yükleniyor…</div></div>
            ) : (
              <HedeflenenCirTrafikTable
                gaMonthlySummary={monthly.gaMonthlySummary}
                allCostsByMonth={monthly.allCostsByMonth}
                targetCost={400000}
                targetCr={0.16}
                sessionsBoost={1.15}
                maxSessions={67500}
                maxRevenue={1500000}
              />
            )}
          </div>

          {/* Disclaimer */}
          <div className="p-4 bg-amber-50 rounded-xl border border-amber-100">
            <p className="text-xs text-amber-800">
              <span className="font-bold">Tahmini Performans Projeksiyonları:</span> Bu plan, Türkiye dijital reklam pazarındaki güncel benchmark'lara dayalı tahmini projeksiyonlarla hazırlanmıştır. 
              Gerçek sonuçlar; açık artırma dinamikleri, hedef kitle davranışı, mevsimsellik, kreatif performansı ve pazar koşullarına bağlı olarak değişiklik gösterebilir. 
              Niş parfüm sektöründe online dönüşüm oranları geleneksel e-ticaretten farklılık gösterebilir; discovery set stratejisi funnel optimizasyonu için kritiktir.
            </p>
          </div>

        </div>
      </div>

    </div>
  );
}
